// src/components/admin/page-management/section-forms/GalleryForm.js
import React from 'react';
import ImageUploader from '../ImageUploader';
import VideoUploader from '../VideoUploader';
import { useState } from 'react';
import {
  Eye, EyeOff, Type, Palette, AlignLeft, AlignCenter, AlignRight,
  Sparkles, Wand2, RotateCcw, Sliders, BookOpen, Zap
} from 'lucide-react';
import { Loader, Settings, Image, Layers, Clock } from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';

const SectionTitleSettings = ({ content = {}, updateGallerySetting = () => { } }) => {
  // Move useState outside to prevent re-initialization
  const [activeTab, setActiveTab] = React.useState('content');
  const [showPreview, setShowPreview] = React.useState(true);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [activePreset, setActivePreset] = React.useState(null);

  // Predefined style presets with enhanced styling
  const stylePresets = [
    {
      id: 'modern',
      name: 'Modern & Clean',
      description: 'Professional and sleek',
      settings: {
        titleFontSize: '2.5rem',
        titleColor: '#1f2937',
        titleAlignment: 'center',
        titleFontWeight: '700',
        titleLetterSpacing: '-0.025em'
      }
    },
    {
      id: 'elegant',
      name: 'Elegant & Sophisticated',
      description: 'Refined and classy',
      settings: {
        titleFontSize: '3rem',
        titleColor: '#374151',
        titleAlignment: 'center',
        titleFontWeight: '300',
        titleLetterSpacing: '0.05em'
      }
    },
    {
      id: 'bold',
      name: 'Bold & Impact',
      description: 'Strong and attention-grabbing',
      settings: {
        titleFontSize: '2.75rem',
        titleColor: '#dc2626',
        titleAlignment: 'left',
        titleFontWeight: '900',
        titleLetterSpacing: '0.1em'
      }
    },
    {
      id: 'minimal',
      name: 'Minimal & Simple',
      description: 'Clean and understated',
      settings: {
        titleFontSize: '1.875rem',
        titleColor: '#6b7280',
        titleAlignment: 'left',
        titleFontWeight: '400',
        titleLetterSpacing: '0'
      }
    }
  ];

  const alignmentOptions = [
    { value: 'left', icon: AlignLeft, name: 'Left' },
    { value: 'center', icon: AlignCenter, name: 'Center' },
    { value: 'right', icon: AlignRight, name: 'Right' }
  ];

  const fontSizePresets = [
    { label: 'Small', value: '1.5rem' },
    { label: 'Medium', value: '2rem' },
    { label: 'Large', value: '2.5rem' },
    { label: 'X-Large', value: '3rem' },
    { label: 'Huge', value: '4rem' }
  ];

  const popularColors = [
    { name: 'Classic Black', value: '#000000' },
    { name: 'Elegant Gray', value: '#374151' },
    { name: 'Modern Blue', value: '#1e40af' },
    { name: 'Bold Red', value: '#dc2626' },
    { name: 'Nature Green', value: '#059669' },
    { name: 'Warm Purple', value: '#7c3aed' },
    { name: 'Sunset Orange', value: '#ea580c' },
    { name: 'Soft Pink', value: '#db2777' }
  ];

  // Function to check if current settings match a preset
  const isPresetActive = React.useCallback((preset) => {
    const currentSettings = {
      titleFontSize: content?.titleFontSize,
      titleColor: content?.titleColor,
      titleAlignment: content?.titleAlignment,
      titleFontWeight: content?.titleFontWeight,
      titleLetterSpacing: content?.titleLetterSpacing
    };

    return Object.entries(preset.settings).every(([key, value]) => {
      return currentSettings[key] === value;
    });
  }, [content]);

  // Fixed applyPreset function to maintain tab state and set active preset
  const applyPreset = React.useCallback((preset) => {
    setIsAnimating(true);
    setActivePreset(preset.id);

    Object.entries(preset.settings).forEach(([key, value]) => {
      updateGallerySetting(key, value);
    });

    setTimeout(() => setIsAnimating(false), 300);
    // Don't change tab - keep current activeTab
  }, [updateGallerySetting]);

  const resetToDefaults = React.useCallback(() => {
    setIsAnimating(true);
    setActivePreset(null);
    updateGallerySetting('title', '');
    updateGallerySetting('subtitle', '');
    updateGallerySetting('titleFontSize', '2rem');
    updateGallerySetting('titleColor', '#333333');
    updateGallerySetting('titleAlignment', 'center');
    updateGallerySetting('titleFontWeight', '700');
    updateGallerySetting('titleLetterSpacing', '0');
    setTimeout(() => setIsAnimating(false), 300);
  }, [updateGallerySetting]);

  // Create stable handlers to prevent re-renders
  const handleColorChange = React.useCallback((color) => {
    updateGallerySetting('titleColor', color);
    setActivePreset(null); // Clear active preset when manually changing settings
  }, [updateGallerySetting]);

  const handleFontSizeChange = React.useCallback((size) => {
    updateGallerySetting('titleFontSize', size);
    setActivePreset(null); // Clear active preset when manually changing settings
  }, [updateGallerySetting]);

  const handleAlignmentChange = React.useCallback((alignment) => {
    updateGallerySetting('titleAlignment', alignment);
    setActivePreset(null); // Clear active preset when manually changing settings
  }, [updateGallerySetting]);

  const LivePreview = React.memo(() => (
    <div className="mt-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
      <div className="text-center mb-3">
        <div className="inline-flex items-center space-x-2 bg-white px-3 py-1 rounded-full shadow-sm text-sm">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-gray-600 font-medium">Live Preview</span>
        </div>
      </div>

      <div className={`p-6 bg-white rounded-lg shadow-sm ${isAnimating ? 'animate-pulse' : ''}`}>
        <div style={{ textAlign: content?.titleAlignment || 'center' }}>
          {content?.title ? (
            <h1
              className="transition-all duration-300"
              style={{
                fontSize: content?.titleFontSize || '2rem',
                color: content?.titleColor || '#333333',
                fontWeight: content?.titleFontWeight || '700',
                letterSpacing: content?.titleLetterSpacing || '0',
                lineHeight: '1.2',
                margin: 0
              }}
            >
              {content.title}
            </h1>
          ) : (
            <div className="text-gray-400 text-lg italic">
              Enter a title to see preview
            </div>
          )}

          {content?.subtitle && (
            <p
              className="mt-3 text-gray-600 transition-all duration-300"
              style={{
                textAlign: content?.titleAlignment || 'center',
                margin: '12px 0 0 0'
              }}
            >
              {content.subtitle}
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-2 text-center">
        This is how your section title will look in the gallery
      </p>
    </div>
  ));

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Type className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Section Title Designer</h2>
              <p className="text-indigo-100">Create beautiful section headings with ease</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefaults}
              className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Navigation Tabs */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          {[
            { id: 'content', label: 'Content', icon: BookOpen },
            { id: 'style', label: 'Style Presets', icon: Wand2 },
            { id: 'design', label: 'Design', icon: Palette }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id
                ? 'bg-white text-indigo-600 shadow-md'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <Sparkles className="w-4 h-4 text-indigo-500" />
                  Section Title
                </label>
                <input
                  type="text"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors text-lg font-medium"
                  placeholder="Enter your section title..."
                  value={content?.title || ''}
                  onChange={(e) => updateGallerySetting('title', e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">This will be the main heading of your gallery section</p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <Type className="w-4 h-4 text-indigo-500" />
                  Subtitle
                  <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <input
                  type="text"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                  placeholder="Add a supporting subtitle..."
                  value={content?.subtitle || ''}
                  onChange={(e) => updateGallerySetting('subtitle', e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Optional descriptive text below the title</p>
              </div>
            </div>
          </div>
        )}
        {/* Style Presets Tab */}
        {activeTab === 'style' && (
          <div className="space-y-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <Zap className="w-4 h-4 text-indigo-500" />
                Quick Style Presets
              </label>
              <p className="text-sm text-gray-600 mb-4">Click any preset to instantly apply the style</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {stylePresets.map(preset => {  // ← This was the error - should be stylePresets, not dimensionPresets
                  const isActive = isPresetActive(preset);
                  return (
                    <button
                      key={preset.id}
                      onClick={() => applyPreset(preset)}
                      className={`p-4 rounded-xl border-2 transition-all text-left group hover:shadow-lg ${isActive
                        ? 'border-indigo-500 bg-indigo-50 shadow-lg ring-2 ring-indigo-200'
                        : 'border-gray-200 hover:border-indigo-300'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-medium ${isActive ? 'text-indigo-800' : 'text-gray-800'}`}>
                              {preset.name}
                            </h3>
                            {isActive && (
                              <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                            )}
                          </div>
                          <p className={`text-xs ${isActive ? 'text-indigo-600' : 'text-gray-600'}`}>
                            {preset.description}
                          </p>
                          <div className="mt-2 text-xs">
                            <span className={`inline-block px-2 py-1 rounded ${isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                              {preset.settings.titleFontSize} • {preset.settings.titleFontWeight}
                            </span>
                          </div>
                        </div>
                        {isActive && (
                          <div className="text-indigo-500">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}


        {/* Design Tab */}
        {activeTab === 'design' && (
          <div className="space-y-6">
            {/* Color Selection */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <Palette className="w-4 h-4 text-indigo-500" />
                Title Color
              </label>

              <div className="grid grid-cols-9 gap-2 mb-4">
                {popularColors.map(color => (
                  <button
                    key={color.value}
                    onClick={() => handleColorChange(color.value)}
                    className={`w-full h-10 rounded-lg border-2 transition-all hover:scale-110 ${content?.titleColor === color.value ? 'border-indigo-500 shadow-lg' : 'border-gray-200'
                      }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  >
                    {content?.titleColor === color.value && (
                      <div className="flex items-center justify-center h-full">
                        <svg className="w-4 h-4 text-white drop-shadow" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}

                {/* Color Picker Button */}
                <div className="relative">
                  <input
                    type="color"
                    value={content?.titleColor || '#333333'}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    title="Custom Color Picker"
                  />
                  <div className={`w-full h-10 rounded-lg border-2 transition-all hover:scale-110 flex items-center justify-center ${!popularColors.some(color => color.value === content?.titleColor)
                    ? 'border-indigo-500 shadow-lg'
                    : 'border-gray-200'
                    }`}
                    style={{
                      background: `conic-gradient(from 0deg, #ff0000, #ff8000, #ffff00, #80ff00, #00ff00, #00ff80, #00ffff, #0080ff, #0000ff, #8000ff, #ff00ff, #ff0080, #ff0000)`
                    }}>
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                      <Palette className="w-3 h-3 text-gray-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded border-2 border-gray-300"
                  style={{ backgroundColor: content?.titleColor || '#333333' }}
                ></div>
                <input
                  type="text"
                  value={content?.titleColor || '#333333'}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                  placeholder="#333333"
                />
              </div>
            </div>

            {/* Font Size */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <Sliders className="w-4 h-4 text-indigo-500" />
                Font Size
              </label>

              <div className="grid grid-cols-5 gap-2 mb-4">
                {fontSizePresets.map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => handleFontSizeChange(preset.value)}
                    className={`p-3 text-sm font-medium rounded-lg border-2 transition-all ${content?.titleFontSize === preset.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <input
                type="text"
                value={content?.titleFontSize || '2rem'}
                onChange={(e) => handleFontSizeChange(e.target.value)}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                placeholder="Custom size (e.g., 2.5rem, 40px)"
              />
            </div>

            {/* Text Alignment */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <AlignCenter className="w-4 h-4 text-indigo-500" />
                Text Alignment
              </label>
              <div className="flex bg-gray-100 rounded-xl p-1">
                {alignmentOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleAlignmentChange(option.value)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${content?.titleAlignment === option.value
                      ? 'bg-white text-indigo-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-800'
                      }`}
                  >
                    <option.icon className="w-4 h-4" />
                    {option.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Advanced Typography */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-4">Advanced Typography</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Font Weight</label>
                  <select
                    value={content?.titleFontWeight || '700'}
                    onChange={(e) => {
                      updateGallerySetting('titleFontWeight', e.target.value);
                      setActivePreset(null);
                    }}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                  >
                    <option value="300">Light (300)</option>
                    <option value="400">Normal (400)</option>
                    <option value="500">Medium (500)</option>
                    <option value="600">Semibold (600)</option>
                    <option value="700">Bold (700)</option>
                    <option value="800">Extra Bold (800)</option>
                    <option value="900">Black (900)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Letter Spacing</label>
                  <select
                    value={content?.titleLetterSpacing || '0'}
                    onChange={(e) => {
                      updateGallerySetting('titleLetterSpacing', e.target.value);
                      setActivePreset(null);
                    }}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                  >
                    <option value="-0.05em">Tight</option>
                    <option value="-0.025em">Slightly Tight</option>
                    <option value="0">Normal</option>
                    <option value="0.025em">Slightly Wide</option>
                    <option value="0.05em">Wide</option>
                    <option value="0.1em">Extra Wide</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live Preview at Bottom */}
        {showPreview && <LivePreview />}
      </div>
    </div>
  );
};

const AnimationInteractionSettings = ({ content = {}, updateContent = () => { } }) => {
  const [activeTab, setActiveTab] = React.useState('animations');
  const [showPreview, setShowPreview] = React.useState(true);

  // Animation presets


  const hoverEffectPresets = [
    {
      id: 'lift',
      name: 'Lift Effect',
      description: 'Card lifts with shadow',
      settings: {
        hoverTransform: 'translateY(-8px)',
        hoverShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
        hoverDuration: '300ms'
      }
    },
    {
      id: 'scale',
      name: 'Scale Effect',
      description: 'Card scales up slightly',
      settings: {
        hoverTransform: 'scale(1.05)',
        hoverShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        hoverDuration: '250ms'
      }
    },
    {
      id: 'glow',
      name: 'Glow Effect',
      description: 'Card glows on hover',
      settings: {
        hoverTransform: 'translateY(-2px)',
        hoverShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
        hoverDuration: '300ms'
      }
    },
    {
      id: 'tilt',
      name: 'Tilt Effect',
      description: 'Subtle 3D tilt effect',
      settings: {
        hoverTransform: 'perspective(1000px) rotateX(5deg) rotateY(5deg)',
        hoverShadow: '0 15px 30px -5px rgba(0, 0, 0, 0.2)',
        hoverDuration: '400ms'
      }
    }
  ];
  const isHoverPresetActive = React.useCallback((preset) => {
    const isActive = content?.hoverTransform === preset.settings.hoverTransform &&
      content?.hoverShadow === preset.settings.hoverShadow &&
      content?.hoverDuration === preset.settings.hoverDuration;

    return isActive;
  }, [content?.hoverTransform, content?.hoverShadow, content?.hoverDuration]);// function to apply hover presets
  const applyHoverPreset = React.useCallback((preset) => {
    Object.entries(preset.settings).forEach(([key, value]) => {
      updateContent(key, value);
    });

    // Force a re-render after state updates
    setTimeout(() => {
      // This will trigger a re-render to update the UI
    }, 10);
  }, [updateContent]);// Helper function to update individual settings
  const updateSetting = React.useCallback((key, value) => {
    updateContent(key, value);
  }, [updateContent]);

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Animation & Interaction</h2>
              <p className="text-purple-100">Bring your features to life with stunning animations</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
            >
              {showPreview ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Navigation Tabs */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          {[
            { id: 'animations', label: 'Entry Animations', icon: '🎬' },
            { id: 'hover', label: 'Hover Effects', icon: '✨' }].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id
                  ? 'bg-white text-purple-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                {tab.label}
              </button>
            ))}
        </div>

        {/* Entry Animations Tab */}
        {activeTab === 'animations' && (
          <div className="space-y-6">


            {/* Custom Animation Settings */}
            <div className="border-t pt-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                Custom Animation Settings
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Animation Type</label>
                  <select
                    value={content?.animationType || 'fade'}
                    onChange={(e) => updateSetting('animationType', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                  >
                    <option value="none">No Animation</option>
                    <option value="fade">Fade In</option>
                    <option value="slideUp">Slide Up</option>
                    <option value="slideDown">Slide Down</option>
                    <option value="slideLeft">Slide Left</option>
                    <option value="slideRight">Slide Right</option>
                    <option value="zoomIn">Zoom In</option>
                    <option value="zoomOut">Zoom Out</option>
                    <option value="bounce">Bounce In</option>
                    <option value="flip">Flip In</option>
                    <option value="rotate">Rotate In</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Animation Trigger</label>
                  <select
                    value={content?.animationTrigger || 'scroll'}
                    onChange={(e) => updateSetting('animationTrigger', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                  >
                    <option value="scroll">On Scroll Into View</option>
                    <option value="load">On Page Load</option>
                    <option value="hover">On Section Hover</option>
                    <option value="click">On Click</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duration (ms)</label>
                  <input
                    type="number"
                    value={content?.animationDuration || 600}
                    onChange={(e) => updateSetting('animationDuration', parseInt(e.target.value))}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                    min="100"
                    max="2000"
                  />
                  <input
                    type="range"
                    min="100"
                    max="2000"
                    step="50"
                    value={content?.animationDuration || 600}
                    onChange={(e) => updateSetting('animationDuration', parseInt(e.target.value))}
                    className="w-full mt-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stagger Delay (ms)</label>
                  <input
                    type="number"
                    value={content?.staggerDelay || 100}
                    onChange={(e) => updateSetting('staggerDelay', parseInt(e.target.value))}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                    min="0"
                    max="500"
                  />
                  <input
                    type="range"
                    min="0"
                    max="500"
                    step="25"
                    value={content?.staggerDelay || 100}
                    onChange={(e) => updateSetting('staggerDelay', parseInt(e.target.value))}
                    className="w-full mt-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Initial Delay (ms)</label>
                  <input
                    type="number"
                    value={content?.initialDelay || 0}
                    onChange={(e) => updateSetting('initialDelay', parseInt(e.target.value))}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                    min="0"
                    max="2000"
                    step="100"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Animation Easing</label>
                <select
                  value={content?.animationEasing || 'ease-out'}
                  onChange={(e) => updateSetting('animationEasing', e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                >
                  <option value="linear">Linear</option>
                  <option value="ease">Ease</option>
                  <option value="ease-in">Ease In</option>
                  <option value="ease-out">Ease Out</option>
                  <option value="ease-in-out">Ease In Out</option>
                  <option value="ease-back">Ease Back</option>
                  <option value="ease-bounce">Ease Bounce</option>
                  <option value="ease-elastic">Ease Elastic</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Hover Effects Tab */}
        {activeTab === 'hover' && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Hover Effect Presets</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">Add interactive hover effects to engage users</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {hoverEffectPresets.map(preset => {
                  const isActive = isHoverPresetActive(preset);

                  return (
                    <button
                      key={preset.id}
                      onClick={() => applyHoverPreset(preset)}
                      className={`p-4 rounded-xl border-2 transition-all text-left group hover:shadow-lg ${isActive
                        ? 'border-pink-500 bg-pink-50 shadow-lg ring-2 ring-pink-200'
                        : 'border-gray-200 hover:border-pink-300'
                        }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className={`font-semibold ${isActive ? 'text-pink-800' : 'text-gray-800'}`}>
                              {preset.name}
                            </h4>
                            {isActive && (
                              <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                            )}
                          </div>
                          <p className={`text-sm ${isActive ? 'text-pink-600' : 'text-gray-600'}`}>
                            {preset.description}
                          </p>
                          <div className="mt-2">
                            <span className={`text-xs px-2 py-1 rounded ${isActive ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                              {preset.settings.hoverDuration}
                            </span>
                          </div>
                        </div>
                        {isActive && (
                          <div className="text-pink-500 ml-2">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Hover Settings */}
            <div className="border-t pt-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                Custom Hover Settings
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hover Transform</label>
                  <input
                    type="text"
                    value={content?.hoverTransform || 'translateY(-4px)'}
                    onChange={(e) => updateSetting('hoverTransform', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                    placeholder="e.g., translateY(-4px), scale(1.05)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hover Duration</label>
                  <select
                    value={content?.hoverDuration || '300ms'}
                    onChange={(e) => updateSetting('hoverDuration', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                  >
                    <option value="150ms">150ms (Fast)</option>
                    <option value="250ms">250ms (Normal)</option>
                    <option value="300ms">300ms (Smooth)</option>
                    <option value="400ms">400ms (Slow)</option>
                    <option value="600ms">600ms (Very Slow)</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Hover Shadow</label>
                <input
                  type="text"
                  value={content?.hoverShadow || '0 10px 15px -3px rgba(0, 0, 0, 0.1)'}
                  onChange={(e) => updateSetting('hoverShadow', e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                  placeholder="e.g., 0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={content?.hoverBackgroundChange !== false}
                      onChange={(e) => updateSetting('hoverBackgroundChange', e.target.checked)}
                      className="mr-2"
                    />
                    Change Background on Hover
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={content?.hoverBorderChange !== false}
                      onChange={(e) => updateSetting('hoverBorderChange', e.target.checked)}
                      className="mr-2"
                    />
                    Change Border on Hover
                  </label>
                </div>
              </div>

              {/* Additional hover settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hover Background Color</label>
                  <input
                    type="color"
                    value={content?.hoverBackgroundColor || '#f3f4f6'}
                    onChange={(e) => updateSetting('hoverBackgroundColor', e.target.value)}
                    className="w-full p-1 border-2 border-gray-200 rounded-lg h-12"
                    disabled={!content?.hoverBackgroundChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hover Border Color</label>
                  <input
                    type="color"
                    value={content?.hoverBorderColor || '#e5e7eb'}
                    onChange={(e) => updateSetting('hoverBorderColor', e.target.value)}
                    className="w-full p-1 border-2 border-gray-200 rounded-lg h-12"
                    disabled={!content?.hoverBorderChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Animation Fill Mode</label>
                  <select
                    value={content?.animationFillMode || 'both'}
                    onChange={(e) => updateSetting('animationFillMode', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                  >
                    <option value="none">None</option>
                    <option value="forwards">Forwards</option>
                    <option value="backwards">Backwards</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hover Timing Function</label>
                  <select
                    value={content?.hoverTimingFunction || 'ease'}
                    onChange={(e) => updateSetting('hoverTimingFunction', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                  >
                    <option value="ease">Ease</option>
                    <option value="ease-in">Ease In</option>
                    <option value="ease-out">Ease Out</option>
                    <option value="ease-in-out">Ease In Out</option>
                    <option value="linear">Linear</option>
                    <option value="cubic-bezier(0.4, 0, 0.2, 1)">Custom Smooth</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live Preview */}
        {showPreview && activeTab === 'animations' && (
          <div className="mt-6 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
            <div className="text-center mb-4">
              <div className="inline-flex items-center space-x-2 bg-white px-3 py-2 rounded-full shadow-sm text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-gray-600 font-medium">Animation Preview</span>

              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`preview-card bg-white p-4 rounded-lg shadow-sm border-2 border-gray-200 text-center transition-all ${content?.animationType === 'fade' ? 'animate-fadeIn' :
                    content?.animationType === 'slideUp' ? 'animate-slideUp' :
                      content?.animationType === 'slideDown' ? 'animate-slideDown' :
                        content?.animationType === 'slideLeft' ? 'animate-slideLeft' :
                          content?.animationType === 'slideRight' ? 'animate-slideRight' :
                            content?.animationType === 'zoomIn' ? 'animate-zoomIn' :
                              content?.animationType === 'zoomOut' ? 'animate-zoomOut' :
                                content?.animationType === 'bounce' ? 'animate-bounce' :
                                  content?.animationType === 'flip' ? 'animate-flip' :
                                    content?.animationType === 'rotate' ? 'animate-rotate' :
                                      ''
                    }`}
                  style={{
                    animationDelay: `${(content?.initialDelay || 0) + (i - 1) * (content?.staggerDelay || 100)}ms`,
                    animationDuration: `${content?.animationDuration || 600}ms`,
                    animationTimingFunction: content?.animationEasing === 'ease-back' ? 'cubic-bezier(0.175, 0.885, 0.32, 1.275)' :
                      content?.animationEasing === 'ease-bounce' ? 'cubic-bezier(0.68, -0.55, 0.265, 1.55)' :
                        content?.animationEasing === 'ease-elastic' ? 'cubic-bezier(0.175, 0.885, 0.32, 1.275)' :
                          content?.animationEasing || 'ease-out',
                    animationFillMode: content?.animationFillMode || 'both',
                    transform: 'translateY(0)', // Base transform
                    transition: `all ${content?.hoverDuration || '300ms'} ease`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    // Apply hover effects on hover
                    '&:hover': {
                      transform: content?.hoverTransform || 'translateY(-10px)',
                      boxShadow: content?.hoverShadow || '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (content?.hoverTransform) {
                      e.currentTarget.style.transform = content.hoverTransform;
                    }
                    if (content?.hoverShadow) {
                      e.currentTarget.style.boxShadow = content.hoverShadow;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">✨</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Feature {i}</h3>
                  <p className="text-sm text-gray-600">This is a preview feature card with live animations</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-4 space-y-2">
              <p className="text-xs text-gray-500">
                <strong>Animation:</strong> {content?.animationType || 'fade'} |
                <strong> Duration:</strong> {content?.animationDuration || 600}ms |
                <strong> Easing:</strong> {content?.animationEasing || 'ease-out'}
              </p>
              <p className="text-xs text-gray-500">
                <strong>Stagger Delay:</strong> {content?.staggerDelay || 100}ms |
                <strong> Initial Delay:</strong> {content?.initialDelay || 0}ms |
                <strong> Trigger:</strong> {content?.animationTrigger || 'scroll'}
              </p>
              {content?.hoverTransform && (
                <p className="text-xs text-gray-500">
                  <strong>Hover:</strong> {content.hoverTransform} |
                  <strong> Duration:</strong> {content?.hoverDuration || '300ms'}
                </p>
              )}
            </div>

            {/* Animation CSS for preview */}
            <style jsx>{`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideDown {
        from { opacity: 0; transform: translateY(-30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideLeft {
        from { opacity: 0; transform: translateX(30px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideRight {
        from { opacity: 0; transform: translateX(-30px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes zoomIn {
        from { opacity: 0; transform: scale(0.8); }
        to { opacity: 1; transform: scale(1); }
      }
      @keyframes zoomOut {
        from { opacity: 0; transform: scale(1.2); }
        to { opacity: 1; transform: scale(1); }
      }
      @keyframes bounce {
        0% { opacity: 0; transform: translateY(30px) scale(0.9); }
        50% { opacity: 0.8; transform: translateY(-10px) scale(1.05); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes flip {
        from { opacity: 0; transform: rotateY(90deg); }
        to { opacity: 1; transform: rotateY(0); }
      }
      @keyframes rotate {
        from { opacity: 0; transform: rotate(180deg) scale(0.8); }
        to { opacity: 1; transform: rotate(0) scale(1); }
      }
      .animate-fadeIn { animation-name: fadeIn; }
      .animate-slideUp { animation-name: slideUp; }
      .animate-slideDown { animation-name: slideDown; }
      .animate-slideLeft { animation-name: slideLeft; }
      .animate-slideRight { animation-name: slideRight; }
      .animate-zoomIn { animation-name: zoomIn; }
      .animate-zoomOut { animation-name: zoomOut; }
      .animate-bounce { animation-name: bounce; }
      .animate-flip { animation-name: flip; }
      .animate-rotate { animation-name: rotate; }
    `}</style>
          </div>
        )}

        {/* Hover Effects Preview */}
        {activeTab === 'hover' && showPreview && (
          <div className="mt-6 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
            <div className="text-center mb-4">
              <div className="inline-flex items-center space-x-2 bg-white px-3 py-2 rounded-full shadow-sm text-sm">
                <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                <span className="text-gray-600 font-medium">Hover Effects Preview</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={`${i}-${content?.hoverTransform}-${content?.hoverDuration}`}
                  className="preview-card bg-white p-4 rounded-lg shadow-sm border-2 border-gray-200 text-center cursor-pointer" style={{
                    transition: `all ${content?.hoverDuration || '300ms'} ${content?.hoverTimingFunction || 'ease'}`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    backgroundColor: '#ffffff',
                    borderColor: '#e5e7eb',
                    transform: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (content?.hoverTransform) {
                      e.currentTarget.style.transform = content.hoverTransform;
                    }
                    if (content?.hoverShadow) {
                      e.currentTarget.style.boxShadow = content.hoverShadow;
                    }
                    if (content?.hoverBackgroundChange && content?.hoverBackgroundColor) {
                      e.currentTarget.style.backgroundColor = content.hoverBackgroundColor;
                    }
                    if (content?.hoverBorderChange && content?.hoverBorderColor) {
                      e.currentTarget.style.borderColor = content.hoverBorderColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">✨</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Hover Me {i}</h3>
                  <p className="text-sm text-gray-600">Move your mouse over this card to see the hover effect</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-4 space-y-2">
              <p className="text-xs text-gray-500">
                <strong>Transform:</strong> {content?.hoverTransform || 'translateY(-4px)'} |
                <strong> Duration:</strong> {content?.hoverDuration || '300ms'} |
                <strong> Timing:</strong> {content?.hoverTimingFunction || 'ease'}
              </p>
              {content?.hoverShadow && (
                <p className="text-xs text-gray-500">
                  <strong>Shadow:</strong> {content.hoverShadow}
                </p>
              )}
              <p className="text-xs text-gray-400 italic">
                Hover over the cards above to see the live effect!
              </p>
            </div>
          </div>
        )}


      </div>
    </div>
  )
};


const LoadingAnimationCustomization = ({ content, updateGallerySetting, pageSlug }) => {
  const [showPreview, setShowPreview] = React.useState(true);
  const [activePreset, setActivePreset] = React.useState(null);

  // Animation presets
  const animationPresets = [
    {
      id: 'spinner',
      name: 'Classic Spinner',
      description: 'Traditional rotating spinner',
      settings: {
        loadingAnimationType: 'default',
        loadingAnimationSpeed: 'normal'
      }
    },
    {
      id: 'pulse',
      name: 'Pulse Effect',
      description: 'Gentle pulsing animation',
      settings: {
        loadingAnimationType: 'pulse',
        loadingAnimationSpeed: 'normal'
      }
    },
    {
      id: 'skeleton',
      name: 'Skeleton Loading',
      description: 'Modern skeleton placeholders',
      settings: {
        loadingAnimationType: 'skeleton',
        loadingAnimationSpeed: 'normal'
      }
    },
    {
      id: 'custom',
      name: 'Custom Animation',
      description: 'Upload your own GIF/image',
      settings: {
        loadingAnimationType: 'custom',
        loadingAnimationSpeed: 'normal'
      }
    }
  ];

  const speedOptions = [
    { value: 'slow', label: 'Slow', duration: '2s', description: 'Relaxed pace' },
    { value: 'normal', label: 'Normal', duration: '1s', description: 'Standard speed' },
    { value: 'fast', label: 'Fast', duration: '0.5s', description: 'Quick animation' }
  ];

  const applyPreset = (preset) => {
    console.log('Applying preset:', preset.id, preset.settings); // Debug log

    // First update the local state
    setActivePreset(preset.id);

    // Then update each setting
    Object.entries(preset.settings).forEach(([key, value]) => {
      console.log('Setting:', key, '=', value); // Debug log
      updateGallerySetting(key, value);
    });

    // Force re-render after a short delay to ensure state is updated
    setTimeout(() => {
      console.log('Active preset after update:', preset.id);
      console.log('Current content type:', content?.loadingAnimationType);
    }, 100);
  };

  const isPresetActive = (preset) => {
    // Check if this specific preset is currently active
    return activePreset === preset.id ||
      (activePreset === null && content?.loadingAnimationType === preset.settings.loadingAnimationType);
  };



  const getAnimationDuration = () => {
    return content?.loadingAnimationSpeed === 'fast' ? '0.5s' :
      content?.loadingAnimationSpeed === 'slow' ? '2s' : '1s';
  };

  const renderAnimationPreview = () => {
    const animationType = content?.loadingAnimationType || 'default';
    const duration = getAnimationDuration();

    console.log('Animation Type:', animationType); // Debug log

    switch (animationType) {
      case 'custom':
        if (content?.customLoadingImage) {
          return (
            <div>
              <div
                className="inline-flex items-center justify-center p-6 rounded-lg relative"
                style={{
                  backgroundColor: content?.loadingBackgroundColor || '#ffffff',
                  opacity: content?.loadingBackgroundOpacity || 0.9,
                  minWidth: '120px',
                  minHeight: '80px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <img
                  src={content.customLoadingImage}
                  alt="Custom loading animation"
                  style={{
                    width: content?.loadingImageSize || '48px',
                    height: content?.loadingImageSize || '48px',
                    maxWidth: content?.loadingImageSize || '48px',
                    maxHeight: content?.loadingImageSize || '48px',
                    objectFit: 'contain',
                    animation: `spin ${duration} linear infinite`
                  }}
                />
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Size: {content?.loadingImageSize || '48px'}
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Background: {content?.loadingBackgroundColor || '#ffffff'}
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  Opacity: {Math.round((content?.loadingBackgroundOpacity ?? 0.9) * 100)}%
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="text-gray-500 text-sm">
              <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-2">
                <Image className="w-6 h-6 text-gray-400" />
              </div>
              Upload a custom image to see preview
            </div>
          );
        }

      case 'skeleton':
        return (
          <div className="space-y-2 w-48">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="h-4 rounded animate-pulse"
                style={{
                  backgroundColor: content?.skeletonColor || '#f0f0f0',
                  width: `${Math.random() * 40 + 60}%`,
                  animationDuration: duration
                }}
              ></div>
            ))}
          </div>
        );

      case 'pulse':
        return (
          <div
            className="w-12 h-12 bg-blue-500 rounded-full animate-pulse"
            style={{
              animationDuration: duration
            }}
          ></div>
        );

      case 'default':
      default:
        return (
          <div
            className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"
            style={{
              animationDuration: duration
            }}
          ></div>
        );
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Loader className="w-5 h-5 animate-spin" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Loading Animation Studio</h2>
              <p className="text-blue-100">Create engaging loading experiences for your gallery</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
            >
              {showPreview ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Quick Presets */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-800">Quick Animation Presets</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">Choose from professionally designed loading animations</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {animationPresets.map(preset => {
              const isActive = isPresetActive(preset);

              return (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(preset)}
                  className={`p-4 rounded-xl border-2 transition-all text-left group hover:shadow-lg ${isActive
                    ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-blue-300'
                    }`}
                >
                  <div className="text-center mb-3">
                    <div className={`text-3xl mb-2 ${isActive ? 'animate-pulse' : ''}`}>
                      {/* Icon representation for each preset */}
                      {preset.id === 'spinner'}
                      {preset.id === 'pulse'}
                      {preset.id === 'skeleton'}
                      {preset.id === 'custom'}
                    </div>
                    <div className="flex items-center gap-2 justify-center">
                      <h4 className={`font-semibold text-sm ${isActive ? 'text-blue-800' : 'text-gray-800'}`}>
                        {preset.name}
                      </h4>
                      {isActive && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      )}
                    </div>

                  </div>
                  <p className={`text-xs text-center ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
                    {preset.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Animation Speed Selection */}
        <div className="mb-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-800">Animation Speed</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {speedOptions.map(option => (
              <button
                key={option.value}
                onClick={() => updateGallerySetting('loadingAnimationSpeed', option.value)}
                className={`p-4 rounded-lg border-2 transition-all ${content?.loadingAnimationSpeed === option.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
              >
                <div className="text-center">
                  <div className="font-semibold">{option.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{option.duration}</div>
                  <div className="text-xs mt-2">{option.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Animation Upload - Only show when custom is selected */}
        {activePreset === 'custom' && (
          <div className="mb-8 p-6 bg-white rounded-xl border-2 border-dashed border-gray-300">
            <div className="flex items-center gap-2 mb-4">
              <Image className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-800">Custom Loading Animation</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Upload Section */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Upload Custom Animation
                </label>

                <input
                  type="text"
                  value={content?.customLoadingImage || ''}
                  onChange={(e) => updateGallerySetting('customLoadingImage', e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors mb-3"
                  placeholder="Enter image/GIF URL or upload below"
                />

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer">
                  <div className="text-gray-500">
                    <ImageUploader
                      onImageUpload={(path) => updateGallerySetting('customLoadingImage', path)}
                      pageSlug={pageSlug}
                    />
                    <p className="text-xs text-gray-400 mt-1">Supports GIF, PNG, SVG</p>
                  </div>
                </div>
              </div>

              {/* Settings Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Animation Settings</h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Size: {parseInt(content?.loadingImageSize) || 48}px
                    </label>
                    <input
                      type="range"
                      min="24"
                      max="200"
                      value={parseInt(content?.loadingImageSize) || 48}
                      onChange={(e) => updateGallerySetting('loadingImageSize', `${e.target.value}px`)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>24px</span>
                      <span>200px</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                    <div className="flex items-center gap-3">
                      <ColorPicker
                        color={content?.loadingBackgroundColor || '#ffffff'}
                        onChange={(color) => updateGallerySetting('loadingBackgroundColor', color)}
                        className="w-12 h-10 border-2 border-gray-200 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={content?.loadingBackgroundColor || '#ffffff'}
                        onChange={(e) => updateGallerySetting('loadingBackgroundColor', e.target.value)}
                        className="flex-1 p-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Opacity: {Math.round((content?.loadingBackgroundOpacity ?? 0.9) * 100)}%
                    </label>
                    <input
                      type="range"
                      value={parseFloat(content?.loadingBackgroundOpacity ?? 0.9)}
                      onChange={(e) => updateGallerySetting('loadingBackgroundOpacity', parseFloat(e.target.value))}
                      className="w-full"
                      min="0"
                      max="1"
                      step="0.05"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activePreset === 'pulse' && (
          <div className="mb-8 p-6 bg-white rounded-xl border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Loader className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-800">Pulse Animation Settings</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Pulse Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={content?.pulseColor || '#4f46e5'}
                    onChange={(e) => updateGallerySetting('pulseColor', e.target.value)}
                    className="w-12 h-10 border-2 border-gray-200 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={content?.pulseColor || '#4f46e5'}
                    onChange={(e) => updateGallerySetting('pulseColor', e.target.value)}
                    className="flex-1 p-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Pulse Duration</label>
                <input
                  type="number"
                  value={content?.pulseDuration || 1000}
                  onChange={(e) => updateGallerySetting('pulseDuration', e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                  placeholder="Duration in ms"
                />
              </div>
            </div>
            <div className="mt-6 p-4 border-2 border-gray-200 rounded-xl bg-gray-50">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Pulse Animation Preview</h4>
              <div className="flex items-center justify-center space-x-4">
                <div
                  className="w-12 h-12 rounded-full"
                  style={{
                    backgroundColor: content?.pulseColor || '#4f46e5',
                    animation: `pulse ${content?.pulseDuration || 1000}ms infinite`
                  }}
                ></div>
                <div className="text-sm text-gray-600">
                  <p>Pulse Color: {content?.pulseColor || '#4f46e5'}</p>
                  <p>Duration: {content?.pulseDuration || 1000} ms</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activePreset === 'spinner' && (
          <div className="mb-8 p-6 bg-white rounded-xl border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Loader className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-800">Spinner Animation Settings</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Spinner Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={content?.spinnerColor || '#4f46e5'}
                    onChange={(e) => updateGallerySetting('spinnerColor', e.target.value)}
                    className="w-12 h-10 border-2 border-gray-200 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={content?.spinnerColor || '#4f46e5'}
                    onChange={(e) => updateGallerySetting('spinnerColor', e.target.value)}
                    className="flex-1 p-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Spinner Size</label>
                <input
                  type="number"
                  value={content?.spinnerSize || 48}
                  onChange={(e) => updateGallerySetting('spinnerSize', e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                  placeholder="Size in px"
                />
              </div>
            </div>
            <div className="mt-6 p-4 border-2 border-gray-200 rounded-xl bg-gray-50">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Spinner Animation Preview</h4>
              <div className="flex items-center justify-center space-x-4">
                <div
                  className="w-12 h-12 rounded-full border-4 border-t-4"
                  style={{
                    borderColor: `${content?.spinnerColor || '#4f46e5'} transparent transparent transparent`,
                    borderWidth: `${content?.spinnerSize || 48}px`,
                    animation: `spin ${getAnimationDuration()} linear infinite`
                  }}
                ></div>
                <div className="text-sm text-gray-600">
                  <p>Spinner Color: {content?.spinnerColor || '#4f46e5'}</p>
                  <p>Size: {content?.spinnerSize || 48}px</p>
                  <p>Duration: {getAnimationDuration()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Skeleton Loading Options - Only show when skeleton is selected */}
        {activePreset === 'skeleton' && (
          <div className="mb-8 p-6 bg-white rounded-xl border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-800">Skeleton Loading Customization</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Base Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={content?.skeletonColor || '#f0f0f0'}
                    onChange={(e) => updateGallerySetting('skeletonColor', e.target.value)}
                    className="w-12 h-10 border-2 border-gray-200 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={content?.skeletonColor || '#f0f0f0'}
                    onChange={(e) => updateGallerySetting('skeletonColor', e.target.value)}
                    className="flex-1 p-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Highlight Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={content?.skeletonHighlightColor || '#e0e0e0'}
                    onChange={(e) => updateGallerySetting('skeletonHighlightColor', e.target.value)}
                    className="w-12 h-10 border-2 border-gray-200 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={content?.skeletonHighlightColor || '#e0e0e0'}
                    onChange={(e) => updateGallerySetting('skeletonHighlightColor', e.target.value)}
                    className="flex-1 p-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Skeleton Preview */}
            <div className="mt-6 p-4 border-2 border-gray-200 rounded-xl bg-gray-50">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Skeleton Preview</h4>
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div
                      className="h-4 rounded"
                      style={{
                        backgroundColor: content?.skeletonColor || '#f0f0f0',
                        width: `${Math.random() * 40 + 60}%`,
                        animationDuration: getAnimationDuration()
                      }}
                    ></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Loading Text Settings */}
        <div className="mb-8 p-6 bg-white rounded-xl border-2 border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-800">Loading Text Settings</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Loading Message</label>
              <input
                type="text"
                value={content?.loadingText || 'Loading...'}
                onChange={(e) => updateGallerySetting('loadingText', e.target.value)}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                placeholder="Loading..."
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={content?.showLoadingText !== false}
                  onChange={(e) => updateGallerySetting('showLoadingText', e.target.checked)}
                  className="mr-3 w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Show Loading Text</span>
              </label>
            </div>
          </div>
        </div>

        {/* Global Preview */}
        {showPreview && activePreset === 'custom' && (
          <div className="mt-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
            <div className="text-center mb-4">
              <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-gray-700 font-medium">Loading Animation Preview</span>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
              <div className="flex flex-col items-center justify-center space-y-4">
                {/* Animation Preview based on type */}
                {renderAnimationPreview()}

                {content?.showLoadingText !== false && (
                  <p className="text-gray-600 font-medium">
                    {content?.loadingText || 'Loading...'}
                  </p>
                )}
              </div>
            </div>

            <div className="text-center mt-4 space-y-1">
              <p className="text-xs text-gray-500">
                <strong>Type:</strong> {content?.loadingAnimationType || 'default'} |
                <strong> Speed:</strong> {content?.loadingAnimationSpeed || 'normal'}
              </p>
              <p className="text-xs text-gray-400 italic">
                This is how your loading animation will appear to users
              </p>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes loadingPreview {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};



const GalleryDimensions = ({ content = {}, updateGallerySetting = () => { } }) => {
  const [activeTab, setActiveTab] = React.useState('dimensions');
  const [showPreview, setShowPreview] = React.useState(true);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [activePreset, setActivePreset] = React.useState(null);

  // Preset dimension configurations
  const dimensionPresets = [
    {
      id: 'fullwidth',
      name: 'Full Width',
      description: 'Spans the entire container width',
      settings: {
        galleryWidth: '100%',
        galleryHeight: 'auto',
        galleryMargin: '0',
        galleryPadding: '1rem'
      }
    },
    {
      id: 'contained',
      name: 'Contained',
      description: 'Centered with max width',
      settings: {
        galleryWidth: '100%',
        galleryHeight: 'auto',
        galleryMargin: '0 auto',
        galleryPadding: '2rem',
        maxWidth: '1200px'
      }
    },
    {
      id: 'compact',
      name: 'Compact',
      description: 'Smaller, focused layout',
      settings: {
        galleryWidth: '90%',
        galleryHeight: 'auto',
        galleryMargin: '1rem auto',
        galleryPadding: '1rem',
        maxWidth: '800px'
      }
    },
    {
      id: 'hero',
      name: 'Hero Section',
      description: 'Full viewport height',
      settings: {
        galleryWidth: '100%',
        galleryHeight: '100vh',
        galleryMargin: '0',
        galleryPadding: '2rem'
      }
    }
  ];

  // Quick size presets
  const sizePresets = {
    width: [
      { label: 'Full', value: '100%' },
      { label: '90%', value: '90%' },
      { label: '80%', value: '80%' },
      { label: '1200px', value: '1200px' },
      { label: '800px', value: '800px' },
      { label: 'Auto', value: 'auto' }
    ],
    height: [
      { label: 'Auto', value: 'auto' },
      { label: '50vh', value: '50vh' },
      { label: '75vh', value: '75vh' },
      { label: '100vh', value: '100vh' },
      { label: '500px', value: '500px' },
      { label: '300px', value: '300px' }
    ]
  };

  // Spacing presets
  const spacingPresets = [
    { label: 'None', value: '0' },
    { label: 'XS', value: '0.25rem' },
    { label: 'SM', value: '0.5rem' },
    { label: 'MD', value: '1rem' },
    { label: 'LG', value: '1.5rem' },
    { label: 'XL', value: '2rem' },
    { label: '2XL', value: '3rem' }
  ];

  const applyPreset = React.useCallback((preset) => {
    setIsAnimating(true);
    setActivePreset(preset.id); // Add this line

    Object.entries(preset.settings).forEach(([key, value]) => {
      updateGallerySetting(key, value);
    });

    setTimeout(() => setIsAnimating(false), 300);
  }, [updateGallerySetting]);

  const resetToDefaults = React.useCallback(() => {
    setIsAnimating(true);
    setActivePreset(null); // Add this line
    updateGallerySetting('galleryWidth', '100%');
    updateGallerySetting('galleryHeight', 'auto');
    updateGallerySetting('galleryMargin', '0');
    updateGallerySetting('galleryPadding', '1rem');
    updateGallerySetting('maxWidth', '');
    updateGallerySetting('minHeight', '');
    updateGallerySetting('galleryMarginIndividual', false);
    updateGallerySetting('galleryPaddingIndividual', false);
    setTimeout(() => setIsAnimating(false), 300);
  }, [updateGallerySetting]);


  const isPresetActive = React.useCallback((preset) => {
    const currentSettings = {
      galleryWidth: content?.galleryWidth,
      galleryHeight: content?.galleryHeight,
      galleryMargin: content?.galleryMargin,
      galleryPadding: content?.galleryPadding,
      maxWidth: content?.maxWidth
    };

    return Object.entries(preset.settings).every(([key, value]) => {
      return currentSettings[key] === value;
    });
  }, [content]);

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Gallery Dimensions & Positioning</h2>
              <p className="text-emerald-100">Control the size, spacing, and layout of your gallery</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefaults}
              className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
            >
              {showPreview ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Navigation Tabs */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          {[
            { id: 'dimensions', label: 'Dimensions' },
            { id: 'spacing', label: 'Spacing' },
            { id: 'responsive', label: 'Responsive' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id
                ? 'bg-white text-emerald-600 shadow-md'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              <span className="text-lg">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dimensions Tab */}
        {activeTab === 'dimensions' && (
          <div className="space-y-6">
            {/* Quick Presets */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-800">Quick Dimension Presets</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">Apply common dimension configurations instantly</p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {dimensionPresets.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset)}
                    className="p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-300 transition-all text-left group hover:shadow-lg"
                  >
                    <div className="text-center mb-3">
                      <h4 className="font-semibold text-gray-800 group-hover:text-emerald-700">
                        {preset.name}
                      </h4>
                    </div>
                    <p className="text-xs text-gray-600 text-center">
                      {preset.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Dimensions */}
            <div className="border-t pt-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100-4m0 4v2m0-6V4" />
                </svg>
                Custom Dimensions
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Width Settings */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h5 className="font-medium mb-3 text-gray-700">Width Configuration</h5>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Quick Width Presets</label>
                    <div className="grid grid-cols-3 gap-2">
                      {sizePresets.width.map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('galleryWidth', preset.value)}
                          className={`p-2 text-xs font-medium rounded border transition-all ${content?.galleryWidth === preset.value
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Custom Width</label>
                    <input
                      type="text"
                      value={content?.galleryWidth || '100%'}
                      onChange={(e) => updateGallerySetting('galleryWidth', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors"
                      placeholder="e.g., 100%, 1200px, auto"
                    />
                  </div>
                </div>

                {/* Height Settings */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h5 className="font-medium mb-3 text-gray-700">Height Configuration</h5>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Quick Height Presets</label>
                    <div className="grid grid-cols-3 gap-2">
                      {sizePresets.height.map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('galleryHeight', preset.value)}
                          className={`p-2 text-xs font-medium rounded border transition-all ${content?.galleryHeight === preset.value
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Custom Height</label>
                    <input
                      type="text"
                      value={content?.galleryHeight || 'auto'}
                      onChange={(e) => updateGallerySetting('galleryHeight', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors"
                      placeholder="e.g., auto, 500px, 50vh"
                    />
                  </div>
                </div>
              </div>

              {/* Constraints */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Max Width</label>
                  <input
                    type="text"
                    value={content?.maxWidth || ''}
                    onChange={(e) => updateGallerySetting('maxWidth', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors"
                    placeholder="e.g., 1200px, 100%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Min Height</label>
                  <input
                    type="text"
                    value={content?.minHeight || ''}
                    onChange={(e) => updateGallerySetting('minHeight', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors"
                    placeholder="e.g., 300px, 20rem"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Spacing Tab */}
        {activeTab === 'spacing' && (
          <div className="space-y-6">
            {/* Margin Settings */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center">
                  <span className="text-orange-600 text-xs font-bold">M</span>
                </div>
                Margin Settings
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* All Margins */}
                <div>
                  <label className="block text-sm font-medium mb-3">All Margins (Shorthand)</label>
                  <div className="mb-4">
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {spacingPresets.map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('galleryMargin', preset.value)}
                          className={`p-2 text-xs font-medium rounded border transition-all ${content?.galleryMargin === preset.value
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={content?.galleryMargin || '0'}
                    onChange={(e) => updateGallerySetting('galleryMargin', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:ring-0 transition-colors"
                    placeholder="e.g., 1rem, 16px, auto"
                  />
                  <p className="text-xs text-gray-500 mt-1">Sets all margins at once</p>
                </div>

                {/* Individual Margins Toggle */}
                <div>
                  <label className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      checked={content?.galleryMarginIndividual || false}
                      onChange={(e) => updateGallerySetting('galleryMarginIndividual', e.target.checked)}
                      className="mr-3 w-4 h-4 text-orange-600 border-2 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Set Individual Margins</span>
                  </label>

                  {content?.galleryMarginIndividual && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Top</label>
                          <input
                            type="text"
                            value={content?.galleryMarginTop || ''}
                            onChange={(e) => updateGallerySetting('galleryMarginTop', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="1rem"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Right</label>
                          <input
                            type="text"
                            value={content?.galleryMarginRight || ''}
                            onChange={(e) => updateGallerySetting('galleryMarginRight', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="auto"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Bottom</label>
                          <input
                            type="text"
                            value={content?.galleryMarginBottom || ''}
                            onChange={(e) => updateGallerySetting('galleryMarginBottom', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="2rem"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Left</label>
                          <input
                            type="text"
                            value={content?.galleryMarginLeft || ''}
                            onChange={(e) => updateGallerySetting('galleryMarginLeft', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="auto"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Padding Settings */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                  <span className="text-blue-600 text-xs font-bold">P</span>
                </div>
                Padding Settings
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* All Padding */}
                <div>
                  <label className="block text-sm font-medium mb-3">All Padding (Shorthand)</label>
                  <div className="mb-4">
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {spacingPresets.map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('galleryPadding', preset.value)}
                          className={`p-2 text-xs font-medium rounded border transition-all ${content?.galleryPadding === preset.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={content?.galleryPadding || '1rem'}
                    onChange={(e) => updateGallerySetting('galleryPadding', e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                    placeholder="e.g., 1rem, 16px"
                  />
                  <p className="text-xs text-gray-500 mt-1">Sets all padding at once</p>
                </div>

                {/* Individual Padding Toggle */}
                <div>
                  <label className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      checked={content?.galleryPaddingIndividual || false}
                      onChange={(e) => updateGallerySetting('galleryPaddingIndividual', e.target.checked)}
                      className="mr-3 w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Set Individual Padding</span>
                  </label>

                  {content?.galleryPaddingIndividual && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Top</label>
                          <input
                            type="text"
                            value={content?.galleryPaddingTop || ''}
                            onChange={(e) => updateGallerySetting('galleryPaddingTop', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="1rem"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Right</label>
                          <input
                            type="text"
                            value={content?.galleryPaddingRight || ''}
                            onChange={(e) => updateGallerySetting('galleryPaddingRight', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="1rem"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Bottom</label>
                          <input
                            type="text"
                            value={content?.galleryPaddingBottom || ''}
                            onChange={(e) => updateGallerySetting('galleryPaddingBottom', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="1rem"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Left</label>
                          <input
                            type="text"
                            value={content?.galleryPaddingLeft || ''}
                            onChange={(e) => updateGallerySetting('galleryPaddingLeft', e.target.value)}
                            className="w-full p-2 border rounded text-sm"
                            placeholder="1rem"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Responsive Tab */}
        {activeTab === 'responsive' && (
          <div className="space-y-6">
            {/* Device Preview Tabs */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Responsive Spacing Controls
              </h4>
              <p className="text-sm text-gray-600 mb-6">Configure different spacing for various screen sizes</p>

              {/* Mobile Settings */}
              <div className="mb-8 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h5 className="font-medium text-blue-900"> Mobile (up to 768px)</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-800">Mobile Margin</label>
                    <div className="mb-3">
                      <div className="grid grid-cols-4 gap-1 mb-2">
                        {spacingPresets.slice(0, 4).map(preset => (
                          <button
                            key={`mobile-margin-${preset.value}`}
                            onClick={() => updateGallerySetting('galleryMarginMobile', preset.value)}
                            className={`p-1.5 text-xs font-medium rounded border transition-all ${content?.galleryMarginMobile === preset.value
                              ? 'border-blue-500 bg-blue-100 text-blue-700'
                              : 'border-blue-200 hover:border-blue-300 bg-white'
                              }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={content?.galleryMarginMobile || ''}
                      onChange={(e) => updateGallerySetting('galleryMarginMobile', e.target.value)}
                      className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 0.5rem, 8px"
                    />
                    <p className="text-xs text-blue-600 mt-1">Optimized for small screens</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-800">Mobile Padding</label>
                    <div className="mb-3">
                      <div className="grid grid-cols-4 gap-1 mb-2">
                        {spacingPresets.slice(0, 4).map(preset => (
                          <button
                            key={`mobile-padding-${preset.value}`}
                            onClick={() => updateGallerySetting('galleryPaddingMobile', preset.value)}
                            className={`p-1.5 text-xs font-medium rounded border transition-all ${content?.galleryPaddingMobile === preset.value
                              ? 'border-blue-500 bg-blue-100 text-blue-700'
                              : 'border-blue-200 hover:border-blue-300 bg-white'
                              }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={content?.galleryPaddingMobile || ''}
                      onChange={(e) => updateGallerySetting('galleryPaddingMobile', e.target.value)}
                      className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 0.5rem, 8px"
                    />
                    <p className="text-xs text-blue-600 mt-1">Compact padding for mobile</p>
                  </div>
                </div>

                {/* Mobile specific dimensions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-800">Mobile Width Override</label>
                    <input
                      type="text"
                      value={content?.galleryWidthMobile || ''}
                      onChange={(e) => updateGallerySetting('galleryWidthMobile', e.target.value)}
                      className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 100%, 95vw (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-800">Mobile Height Override</label>
                    <input
                      type="text"
                      value={content?.galleryHeightMobile || ''}
                      onChange={(e) => updateGallerySetting('galleryHeightMobile', e.target.value)}
                      className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., auto, 40vh (optional)"
                    />
                  </div>
                </div>
                {/* Mobile Preview
                      <div className="text-center">
                  <div className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-3 mb-2">
                    <div className="text-xs font-medium text-blue-700 mb-2">📱 Mobile View</div>
                    <div className="bg-white rounded border" style={{
                      margin: content?.galleryMarginMobile || '0.5rem',
                      padding: content?.galleryPaddingMobile || '0.5rem',
                      minHeight: '60px'
                    }}>
                      <div className="text-xs text-gray-600">Gallery</div>
                    </div>
                  </div>
                  <div className="text-xs text-blue-600">
                    M: {content?.galleryMarginMobile || 'inherit'}<br />
                    P: {content?.galleryPaddingMobile || 'inherit'}
                  </div>
                </div> */}
              </div>

              {/* Tablet Settings */}
              <div className="mb-8 p-5 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h5 className="font-medium text-purple-900">Tablet (768px - 1024px)</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-800">Tablet Margin</label>
                    <div className="mb-3">
                      <div className="grid grid-cols-4 gap-1 mb-2">
                        {spacingPresets.slice(1, 5).map(preset => (
                          <button
                            key={`tablet-margin-${preset.value}`}
                            onClick={() => updateGallerySetting('galleryMarginTablet', preset.value)}
                            className={`p-1.5 text-xs font-medium rounded border transition-all ${content?.galleryMarginTablet === preset.value
                              ? 'border-purple-500 bg-purple-100 text-purple-700'
                              : 'border-purple-200 hover:border-purple-300 bg-white'
                              }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={content?.galleryMarginTablet || ''}
                      onChange={(e) => updateGallerySetting('galleryMarginTablet', e.target.value)}
                      className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 1rem, 16px"
                    />
                    <p className="text-xs text-purple-600 mt-1">Medium screen spacing</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-800">Tablet Padding</label>
                    <div className="mb-3">
                      <div className="grid grid-cols-4 gap-1 mb-2">
                        {spacingPresets.slice(1, 5).map(preset => (
                          <button
                            key={`tablet-padding-${preset.value}`}
                            onClick={() => updateGallerySetting('galleryPaddingTablet', preset.value)}
                            className={`p-1.5 text-xs font-medium rounded border transition-all ${content?.galleryPaddingTablet === preset.value
                              ? 'border-purple-500 bg-purple-100 text-purple-700'
                              : 'border-purple-200 hover:border-purple-300 bg-white'
                              }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={content?.galleryPaddingTablet || ''}
                      onChange={(e) => updateGallerySetting('galleryPaddingTablet', e.target.value)}
                      className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 1rem, 16px"
                    />
                    <p className="text-xs text-purple-600 mt-1">Balanced padding for tablets</p>
                  </div>
                </div>

                {/* Tablet specific dimensions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-800">Tablet Width Override</label>
                    <input
                      type="text"
                      value={content?.galleryWidthTablet || ''}
                      onChange={(e) => updateGallerySetting('galleryWidthTablet', e.target.value)}
                      className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 90%, 800px (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-800">Tablet Height Override</label>
                    <input
                      type="text"
                      value={content?.galleryHeightTablet || ''}
                      onChange={(e) => updateGallerySetting('galleryHeightTablet', e.target.value)}
                      className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., auto, 60vh (optional)"
                    />
                  </div>
                </div>

                {/* Tablet Preview
                <div className="text-center">
                  <div className="bg-purple-50 border-2 border-dashed border-purple-300 rounded-lg p-3 mb-2">
                    <div className="text-xs font-medium text-purple-700 mb-2">📟 Tablet View</div>
                    <div className="bg-white rounded border" style={{
                      margin: content?.galleryMarginTablet || '1rem',
                      padding: content?.galleryPaddingTablet || '1rem',
                      minHeight: '60px'
                    }}>
                      <div className="text-xs text-gray-600">Gallery</div>
                    </div>
                  </div>
                  <div className="text-xs text-purple-600">
                    M: {content?.galleryMarginTablet || 'inherit'}<br />
                    P: {content?.galleryPaddingTablet || 'inherit'}
                  </div>
                </div> */}
              </div>

              {/* Desktop Settings */}
              <div className="mb-8 p-5 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h5 className="font-medium text-green-900">Desktop (1024px+)</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-green-800">Desktop Margin</label>
                    <div className="mb-3">
                      <div className="grid grid-cols-4 gap-1 mb-2">
                        {spacingPresets.slice(2, 6).map(preset => (
                          <button
                            key={`desktop-margin-${preset.value}`}
                            onClick={() => updateGallerySetting('galleryMarginDesktop', preset.value)}
                            className={`p-1.5 text-xs font-medium rounded border transition-all ${content?.galleryMarginDesktop === preset.value
                              ? 'border-green-500 bg-green-100 text-green-700'
                              : 'border-green-200 hover:border-green-300 bg-white'
                              }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={content?.galleryMarginDesktop || ''}
                      onChange={(e) => updateGallerySetting('galleryMarginDesktop', e.target.value)}
                      className="w-full p-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 2rem, 32px"
                    />
                    <p className="text-xs text-green-600 mt-1">Generous spacing for large screens</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-green-800">Desktop Padding</label>
                    <div className="mb-3">
                      <div className="grid grid-cols-4 gap-1 mb-2">
                        {spacingPresets.slice(2, 6).map(preset => (
                          <button
                            key={`desktop-padding-${preset.value}`}
                            onClick={() => updateGallerySetting('galleryPaddingDesktop', preset.value)}
                            className={`p-1.5 text-xs font-medium rounded border transition-all ${content?.galleryPaddingDesktop === preset.value
                              ? 'border-green-500 bg-green-100 text-green-700'
                              : 'border-green-200 hover:border-green-300 bg-white'
                              }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={content?.galleryPaddingDesktop || ''}
                      onChange={(e) => updateGallerySetting('galleryPaddingDesktop', e.target.value)}
                      className="w-full p-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 2rem, 32px"
                    />
                    <p className="text-xs text-green-600 mt-1">Comfortable desktop padding</p>
                  </div>
                </div>

                {/* Desktop specific dimensions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-green-800">Desktop Width Override</label>
                    <input
                      type="text"
                      value={content?.galleryWidthDesktop || ''}
                      onChange={(e) => updateGallerySetting('galleryWidthDesktop', e.target.value)}
                      className="w-full p-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., 1200px, 80% (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-green-800">Desktop Height Override</label>
                    <input
                      type="text"
                      value={content?.galleryHeightDesktop || ''}
                      onChange={(e) => updateGallerySetting('galleryHeightDesktop', e.target.value)}
                      className="w-full p-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-0 transition-colors bg-white"
                      placeholder="e.g., auto, 80vh (optional)"
                    />
                  </div>
                </div>
                {/* Desktop Preview
                  <div className="text-center">
                  <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-lg p-3 mb-2">
                    <div className="text-xs font-medium text-green-700 mb-2">🖥️ Desktop View</div>
                    <div className="bg-white rounded border" style={{
                      margin: content?.galleryMarginDesktop || '2rem',
                      padding: content?.galleryPaddingDesktop || '2rem',
                      minHeight: '60px'
                    }}>
                      <div className="text-xs text-gray-600">Gallery</div>
                    </div>
                  </div>
                  <div className="text-xs text-green-600">
                    M: {content?.galleryMarginDesktop || 'inherit'}<br />
                    P: {content?.galleryPaddingDesktop || 'inherit'}
                  </div>
                </div> */}
              </div>
            </div>

            {/* Responsive Breakpoints Info */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-800 mb-2">💡 Responsive Design Tips</h4>
                  <div className="text-sm text-amber-700 space-y-2">
                    <p><strong>Mobile (0-767px):</strong> Use minimal margins and compact padding for better space utilization</p>
                    <p><strong>Tablet (768-1023px):</strong> Moderate spacing works well for medium-sized screens</p>
                    <p><strong>Desktop (1024px+):</strong> Generous spacing enhances visual hierarchy and readability</p>
                    <p><strong>Override Behavior:</strong> Device-specific dimensions will override the base settings on respective screen sizes</p>
                  </div>
                </div>
              </div>
            </div>


          </div>
        )}
      </div>
    </div>
  )
};

const GalleryForm = ({ content, updateContent, pageSlug }) => {
  const addImage = () => {
    const images = content?.images || [];
    const newImages = [
      ...images,
      {
        url: '',
        alt: '',
        caption: '',
        type: 'image',
        isActive: true,
        clickable: true,
        hoverEffect: 'zoom',
        onClickAction: 'modal',
        width: '',
        height: '',
        objectFit: 'cover',
        // Enhanced container properties
        containerWidth: '',
        containerHeight: '',
        containerPadding: '',
        containerMargin: '',
        containerBackground: 'transparent',
        containerBorderRadius: '',
        containerPosition: 'relative',
        containerZIndex: '',
        containerOverflowHidden: false,
        containerIsolate: false,
        containerShadow: 'none',
        customContainerShadow: '',
        // Video access control
        videoAccess: 'public',
        unlockCondition: 'login',
        unlockPrice: '',
        unlockPassword: '',
        subscriptionLevel: 'basic',
        availableFrom: '',
        availableUntil: '',
        formId: '',
        lockIcon: 'lock',
        customLockIcon: '',
        lockMessage: 'This video is locked',
        lockOverlayColor: '#000000',
        lockOverlayOpacity: 0.8,
        lockTextColor: '#ffffff',
        // Enhanced metadata
        showOverlay: false,
        overlayText: '',
        overlayColor: '#000000',
        priority: 0,
        loadingBehavior: 'lazy',
        customClass: '',
        customStyles: ''
      }
    ];
    updateContent('images', newImages);
  };
  const updateImage = (index, field, value) => {
    const images = [...(content?.images || [])];
    if (!images[index]) return;

    images[index] = {
      ...images[index],
      [field]: value
    };

    updateContent('images', images);
  };

  const removeImage = (index) => {
    const images = [...(content?.images || [])];
    images.splice(index, 1);
    updateContent('images', images);
  };

  // Handle gallery settings
  const updateGallerySetting = (field, value) => {
    console.log(`Updating gallery setting: ${field}`, value, typeof value);

    // Handle special cases for certain fields
    if (field === 'progressiveLoading' && value === true && !content?.itemsPerLoad) {
      updateContent('itemsPerLoad', 6);
    }

    if (field === 'itemsPerLoad') {
      const validValue = Math.max(1, Math.min(20, parseInt(value) || 6));
      updateContent(field, validValue);
      return;
    }

    if (field === 'loadingAnimationType' && value === 'custom' && !content?.customLoadingImage) {
      console.log('Custom loading animation selected - user should upload an image');
    }

    // FIX: Ensure animation settings are properly converted to correct types
    if (field === 'animationDuration') {
      updateContent(field, parseInt(value) || 600);
      return;
    }

    if (field === 'staggerDelay') {
      updateContent(field, parseInt(value) || 100);
      return;
    }

    if (field === 'parallaxSpeed') {
      updateContent(field, parseFloat(value) || 0.5);
      return;
    }

    // FIX: Force update for animation fields
    if (['loadAnimation', 'transitionTiming', 'parallaxEffect', 'parallaxDirection'].includes(field)) {
      updateContent(field, value);

      // Force re-render by dispatching custom event
      setTimeout(() => {
        const event = new CustomEvent('animationUpdate', {
          detail: {
            field,
            value,
            animationSettings: {
              loadAnimation: field === 'loadAnimation' ? value : content?.loadAnimation,
              animationDuration: content?.animationDuration || 600,
              staggerDelay: content?.staggerDelay || 100,
              transitionTiming: field === 'transitionTiming' ? value : content?.transitionTiming,
              parallaxEffect: field === 'parallaxEffect' ? value : content?.parallaxEffect,
              parallaxSpeed: content?.parallaxSpeed || 0.5,
              parallaxDirection: field === 'parallaxDirection' ? value : content?.parallaxDirection
            }
          }
        });
        window.dispatchEvent(event);
      }, 50);
      return;
    }

    updateContent(field, value);
  };
  // Handle grid column updates specifically
  const updateGridColumns = (device, value) => {
    const currentGridColumns = content?.gridColumns || {
      mobile: 1,
      tablet: 2,
      desktop: 3,
      widescreen: 4
    };

    const newGridColumns = {
      ...currentGridColumns,
      [device]: parseInt(value, 10)
    };

    console.log(`Updating ${device} columns to:`, value, 'Full gridColumns:', newGridColumns);
    updateContent('gridColumns', newGridColumns);

    // Force re-render by triggering a state update
    setTimeout(() => {
      const event = new CustomEvent('galleryUpdate', {
        detail: { gridColumns: newGridColumns, displayType: content?.displayType }
      });
      window.dispatchEvent(event);
    }, 100);
  };


  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Gallery Section</h3>

      {/* Gallery Settings */}
      <div className="mb-6 p-4 bg-gray-50 rounded">

        {/* Section Title Settings */}
        <SectionTitleSettings
          content={content}
          updateGallerySetting={updateGallerySetting}
        />





        {/* Advanced Animation Settings */}
        <AnimationInteractionSettings
          content={content}
          updateContent={updateGallerySetting}
        />

        {/* {content?.progressiveLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Items Per Load</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={content?.itemsPerLoad || 6}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    updateGallerySetting('itemsPerLoad', value);
                  }}
                  className="flex-1"
                />
                <input
                  type="number"
                  value={content?.itemsPerLoad || 6}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    const validValue = Math.max(1, Math.min(20, value || 1));
                    updateGallerySetting('itemsPerLoad', validValue);
                  }}
                  className="w-20 p-2 border rounded"
                  min="1"
                  max="20"
                />
              </div>
              <small className="text-gray-500 text-xs mt-1 block">
                Control how many items load at once (1-20). Lower values = faster initial load.
              </small>
            </div>
          </div>
        )} */}




        {/* Loading Animation Customization */}
        <LoadingAnimationCustomization
          content={content}
          updateGallerySetting={updateGallerySetting}
        />

        {/* Gallery Dimensions */}
        <GalleryDimensions
          content={content}
          updateGallerySetting={updateGallerySetting}
        />

        {/* Layout Settings */}
        <div className="mb-6 p-4 bg-white rounded border">
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-800">Layout Settings</h4>
                <p className="text-sm text-gray-600">Configure how your gallery items are displayed and spaced</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Display Type Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">

                  <label className="text-sm font-semibold text-gray-700">Display Type</label>
                </div>

                {/* Display Type Options */}
                <div className="space-y-3">
                  {[
                    {
                      value: 'grid',
                      label: 'Grid Layout',
                      description: 'Structured rows and columns',
                    },
                    {
                      value: 'carousel',
                      label: 'Carousel',
                      description: 'Horizontal scrolling slideshow',
                    },
                    {
                      value: 'flex',
                      label: 'Flexible Layout',
                      description: 'Adaptive spacing and wrapping',
                    },
                    {
                      value: 'masonry',
                      label: 'Masonry Grid',
                      description: 'Pinterest-style layout',
                    }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateGallerySetting('displayType', option.value)}
                      className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left hover:shadow-lg group ${(content?.displayType || 'grid') === option.value
                        ? 'border-purple-500 bg-purple-50 shadow-lg ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center gap-3">

                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h5 className={`font-semibold ${(content?.displayType || 'grid') === option.value ? 'text-purple-800' : 'text-gray-800'
                              }`}>
                              {option.label}
                            </h5>
                            {(content?.displayType || 'grid') === option.value && (
                              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                            )}
                          </div>
                          <p className={`text-xs ${(content?.displayType || 'grid') === option.value ? 'text-purple-600' : 'text-gray-500'
                            }`}>
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Gap Size Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">

                  <label className="text-sm font-semibold text-gray-700">Gap Size</label>
                </div>

                {/* Quick Gap Presets */}
                <div className="mb-4">
                  <p className="text-xs text-gray-600 mb-3">Quick gap presets</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Tight', value: '0.5rem', description: '8px' },
                      { label: 'Normal', value: '1rem', description: '16px' },
                      { label: 'Comfortable', value: '1.5rem', description: '24px' },
                      { label: 'Spacious', value: '2rem', description: '32px' },
                      { label: 'Generous', value: '3rem', description: '48px' },
                      { label: 'Wide', value: '4rem', description: '64px' }
                    ].map(preset => (
                      <button
                        key={preset.value}
                        onClick={() => updateGallerySetting('gapSize', preset.value)}
                        className={`p-3 text-xs font-medium rounded-lg border-2 transition-all hover:shadow-md ${(content?.gapSize || '1.5rem') === preset.value
                          ? 'border-pink-500 bg-pink-50 text-pink-700 shadow-md'
                          : 'border-gray-200 hover:border-pink-300 text-gray-700 hover:bg-pink-50'
                          }`}
                      >
                        <div className="font-semibold">{preset.label}</div>
                        <div className="text-gray-500 text-xs mt-1">{preset.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Gap Input */}
                <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-4 rounded-xl border border-pink-200">
                  <label className="block text-sm font-medium mb-3 text-pink-800">Custom Gap Size</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={content?.gapSize || '1.5rem'}
                      onChange={(e) => updateGallerySetting('gapSize', e.target.value)}
                      className="w-full p-3 pr-10 border-2 border-pink-200 rounded-lg focus:border-pink-500 focus:ring-0 transition-colors bg-white placeholder-pink-300"
                      placeholder="e.g., 1.5rem, 24px, 2%"
                    />

                  </div>
                  <p className="text-xs text-pink-600 mt-2">
                    Accepts rem, px, %, em, vw, vh units
                  </p>
                </div>


              </div>
            </div>

            {/* Layout Preview Section */}
            <div className="mt-8 p-4 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border-2 border-dashed border-gray-300">
              <div className="text-center mb-4">
                <div className="inline-flex items-center space-x-2 bg-white px-3 py-1 rounded-full shadow-sm text-sm">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                  <span className="text-gray-600 font-medium">Layout Preview</span>
                </div>
              </div>

              <div className="flex justify-center">
                <div
                  className={`inline-flex ${(content?.displayType || 'grid') === 'grid' ? 'grid grid-cols-3' :
                    (content?.displayType || 'grid') === 'carousel' ? 'flex overflow-x-auto' :
                      (content?.displayType || 'grid') === 'flex' ? 'flex flex-wrap justify-center' :
                        'grid grid-cols-2'
                    }`}
                  style={{ gap: content?.gapSize || '1.5rem' }}
                >
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div
                      key={i}
                      className={`bg-gradient-to-br from-purple-300 to-pink-300 rounded-lg shadow-sm flex items-center justify-center text-white font-semibold ${(content?.displayType || 'grid') === 'masonry' && i % 3 === 0 ? 'h-16' : 'h-12'
                        } ${(content?.displayType || 'grid') === 'carousel' ? 'flex-shrink-0' : ''
                        }`}
                      style={{
                        width: (content?.displayType || 'grid') === 'carousel' ? '80px' : '60px',
                        minWidth: (content?.displayType || 'grid') === 'carousel' ? '80px' : 'auto'
                      }}
                    >
                      {i}
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-center text-gray-500 mt-3">
                <span className="font-medium text-purple-600">{
                  (content?.displayType || 'grid') === 'grid' ? 'Grid Layout' :
                    (content?.displayType || 'grid') === 'carousel' ? 'Carousel' :
                      (content?.displayType || 'grid') === 'flex' ? 'Flexible Layout' :
                        'Masonry Grid'
                }</span> with <span className="font-medium text-pink-600">{content?.gapSize || '1.5rem'}</span> gap
              </p>
            </div>
          </div>
          {/* Enhanced Grid Layout Settings */}
          {content?.displayType === 'grid' && (
            <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-blue-900">Grid Layout Settings</h5>
                  <p className="text-sm text-blue-700">Configure responsive column behavior for different screen sizes</p>
                </div>
              </div>

              {/* Device-Specific Column Settings */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Mobile Settings */}
                <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h6 className="font-semibold text-gray-800">Mobile</h6>
                      <p className="text-xs text-gray-600">Up to 768px</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Columns</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[1, 2].map(num => (
                        <button
                          key={`mobile-${num}`}
                          onClick={() => updateGridColumns('mobile', num.toString())}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-2 ${(content?.gridColumns?.mobile || 1).toString() === num.toString()
                            ? 'border-green-500 bg-green-50 text-green-700 shadow-md ring-2 ring-green-200'
                            : 'border-gray-200 hover:border-green-300 text-gray-600 hover:bg-green-50'
                            }`}
                        >
                          <div className="text-lg font-bold">{num}</div>
                          <div className="text-xs">Column{num > 1 ? 's' : ''}</div>
                          {/* Visual representation */}
                          <div className={`flex gap-1 mt-1`}>
                            {Array.from({ length: num }).map((_, i) => (
                              <div key={i} className="w-3 h-2 bg-current opacity-60 rounded-sm"></div>
                            ))}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tablet Settings */}
                <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h6 className="font-semibold text-gray-800">Tablet</h6>
                      <p className="text-xs text-gray-600">768px - 1024px</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Columns</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[1, 2, 3].map(num => (
                        <button
                          key={`tablet-${num}`}
                          onClick={() => updateGridColumns('tablet', num.toString())}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-2 ${(content?.gridColumns?.tablet || 2).toString() === num.toString()
                            ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-md ring-2 ring-purple-200'
                            : 'border-gray-200 hover:border-purple-300 text-gray-600 hover:bg-purple-50'
                            }`}
                        >
                          <div className="text-lg font-bold">{num}</div>
                          <div className="text-xs">Column{num > 1 ? 's' : ''}</div>
                          {/* Visual representation */}
                          <div className={`flex gap-1 mt-1`}>
                            {Array.from({ length: num }).map((_, i) => (
                              <div key={i} className="w-2 h-2 bg-current opacity-60 rounded-sm"></div>
                            ))}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Desktop Settings */}
                <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h6 className="font-semibold text-gray-800">Desktop</h6>
                      <p className="text-xs text-gray-600">1024px - 1440px</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Columns</label>
                    <div className="grid grid-cols-5 gap-2">
                      {[1, 2, 3, 4, 5].map(num => (
                        <button
                          key={`desktop-${num}`}
                          onClick={() => updateGridColumns('desktop', num.toString())}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-2 ${(content?.gridColumns?.desktop || 3).toString() === num.toString()
                            ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-300 text-gray-600 hover:bg-blue-50'
                            }`}
                        >
                          <div className="text-lg font-bold">{num}</div>
                          <div className="text-xs">Col{num > 1 ? 's' : ''}</div>
                          {/* Visual representation */}
                          <div className={`flex gap-0.5 mt-1`}>
                            {Array.from({ length: Math.min(num, 4) }).map((_, i) => (
                              <div key={i} className="w-1.5 h-1.5 bg-current opacity-60 rounded-sm"></div>
                            ))}
                            {num > 4 && <div className="text-xs">...</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Widescreen Settings */}
                <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM6 10h12M6 14h12" />
                      </svg>
                    </div>
                    <div>
                      <h6 className="font-semibold text-gray-800">Widescreen</h6>
                      <p className="text-xs text-gray-600">1440px+</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Columns</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[1, 2, 3, 4, 5, 6].map(num => (
                        <button
                          key={`widescreen-${num}`}
                          onClick={() => updateGridColumns('widescreen', num.toString())}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-2 ${(content?.gridColumns?.widescreen || 4).toString() === num.toString()
                            ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md ring-2 ring-orange-200'
                            : 'border-gray-200 hover:border-orange-300 text-gray-600 hover:bg-orange-50'
                            }`}
                        >
                          <div className="text-lg font-bold">{num}</div>
                          <div className="text-xs">Col{num > 1 ? 's' : ''}</div>
                          {/* Visual representation */}
                          <div className={`flex gap-0.5 mt-1`}>
                            {Array.from({ length: Math.min(num, 5) }).map((_, i) => (
                              <div key={i} className="w-1 h-1.5 bg-current opacity-60 rounded-sm"></div>
                            ))}
                            {num > 5 && <div className="text-xs">+</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Grid Preview Section */}
              <div className="mt-8 bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border-2 border-dashed border-blue-300">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm border border-blue-200">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-gray-700 font-medium">Responsive Grid Preview</span>
                  </div>
                </div>

                {/* Device Previews */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { device: 'Mobile', cols: content?.gridColumns?.mobile || 1, color: 'green', icon: '📱' },
                    { device: 'Tablet', cols: content?.gridColumns?.tablet || 2, color: 'purple', icon: '📟' },
                    { device: 'Desktop', cols: content?.gridColumns?.desktop || 3, color: 'blue', icon: '🖥️' },
                    { device: 'Widescreen', cols: content?.gridColumns?.widescreen || 4, color: 'orange', icon: '🖥️' }
                  ].map(({ device, cols, color, icon }) => (
                    <div key={device} className="text-center">
                      <div className="mb-2">
                        <div className="text-lg mb-1">{icon}</div>
                        <div className="text-xs font-medium text-gray-600">{device}</div>
                        <div className="text-xs text-gray-500">{cols} column{cols > 1 ? 's' : ''}</div>
                      </div>
                      <div
                        className={`grid gap-1 mx-auto`}
                        style={{
                          gridTemplateColumns: `repeat(${cols}, 1fr)`,
                          maxWidth: device === 'Mobile' ? '80px' : device === 'Tablet' ? '120px' : device === 'Desktop' ? '150px' : '180px'
                        }}
                      >
                        {Array.from({ length: Math.min(cols * 2, 8) }).map((_, i) => (
                          <div
                            key={i}
                            className={`h-3 bg-${color}-300 rounded-sm opacity-70`}
                            style={{
                              backgroundColor:
                                color === 'green' ? '#86efac' :
                                  color === 'purple' ? '#c084fc' :
                                    color === 'blue' ? '#93c5fd' :
                                      '#fdba74'
                            }}
                          ></div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center mt-4">
                  <p className="text-xs text-gray-500">
                    Grid adapts automatically based on screen size with smooth transitions
                  </p>
                </div>
              </div>


            </div>
          )}
   

{/* Enhanced Masonry Advanced Settings */}
{content?.displayType === 'masonry' && (
  <div className="mt-6 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-6">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
        <div className="relative w-6 h-6">
          <div className="absolute left-0 top-0 w-1.5 h-4 bg-white rounded-sm"></div>
          <div className="absolute left-2 top-1 w-1.5 h-5 bg-white rounded-sm"></div>
          <div className="absolute right-0 top-0.5 w-1.5 h-3.5 bg-white rounded-sm"></div>
        </div>
      </div>
      <div>
        <h4 className="text-xl font-bold text-violet-900">Advanced Masonry Layout</h4>
        <p className="text-sm text-violet-700">Fine-tune your masonry grid with advanced column and layout settings</p>
      </div>
    </div>

    {/* Responsive Column Configuration */}
    <div className="bg-white rounded-xl p-6 border border-violet-200 shadow-sm mb-6">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center">
          <div className="grid grid-cols-3 gap-0.5 w-4 h-4">
            <div className="bg-white rounded-sm h-1.5"></div>
            <div className="bg-white rounded-sm h-2"></div>
            <div className="bg-white rounded-sm h-1"></div>
          </div>
        </div>
        <h5 className="text-lg font-semibold text-gray-800">Responsive Column Setup</h5>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Desktop Columns */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            Desktop Columns (1024px+)
          </label>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
            {/* Column Count Buttons */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              {[2, 3, 4, 5, 6].map(num => (
                <button
                  key={num}
                  onClick={() => updateGallerySetting('masonryColumns', {
                    ...content?.masonryColumns,
                    desktop: parseInt(num)
                  })}
                  className={`p-3 rounded-lg border-2 transition-all text-center hover:shadow-md ${
                    (content?.masonryColumns?.desktop || 3) === num
                      ? 'border-blue-500 bg-blue-100 text-blue-700 shadow-lg ring-2 ring-blue-200'
                      : 'border-blue-200 hover:border-blue-300 bg-white text-blue-700'
                  }`}
                >
                  <div className="font-bold text-lg">{num}</div>
                  <div className="text-xs">Col{num > 1 ? 's' : ''}</div>
                  {/* Visual Grid */}
                  <div 
                    className="grid gap-0.5 mt-2 mx-auto"
                    style={{ 
                      gridTemplateColumns: `repeat(${Math.min(num, 4)}, 1fr)`,
                      width: '20px'
                    }}
                  >
                    {Array.from({length: Math.min(num, 4)}).map((_, i) => (
                      <div key={i} className="w-1 h-1 bg-blue-400 rounded-sm"></div>
                    ))}
                    {num > 4 && <div className="text-xs">+</div>}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Custom Select Fallback */}
            <select
              value={content?.masonryColumns?.desktop || 3}
              onChange={(e) => updateGallerySetting('masonryColumns', {
                ...content?.masonryColumns,
                desktop: parseInt(e.target.value)
              })}
              className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors bg-white"
            >
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
              <option value={5}>5 Columns</option>
              <option value={6}>6 Columns</option>
            </select>
            
            <div className="text-center mt-3">
              <span className="text-sm font-medium text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                Current: {content?.masonryColumns?.desktop || 3} columns
              </span>
            </div>
          </div>
        </div>

        {/* Mobile Columns */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            Mobile Columns (up to 768px)
          </label>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
            {/* Mobile Column Buttons */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[1, 2].map(num => (
                <button
                  key={num}
                  onClick={() => updateGallerySetting('masonryColumns', {
                    ...content?.masonryColumns,
                    mobile: parseInt(num)
                  })}
                  className={`p-4 rounded-lg border-2 transition-all text-center hover:shadow-md ${
                    (content?.masonryColumns?.mobile || 1) === num
                      ? 'border-purple-500 bg-purple-100 text-purple-700 shadow-lg ring-2 ring-purple-200'
                      : 'border-purple-200 hover:border-purple-300 bg-white text-purple-700'
                  }`}
                >
                  
                  <div className="font-bold text-lg">{num}</div>
                  <div className="text-xs mb-2">Column{num > 1 ? 's' : ''}</div>
                  
                  {/* Visual Mobile Grid */}
                  <div 
                    className="grid gap-1 mx-auto"
                    style={{ 
                      gridTemplateColumns: `repeat(${num}, 1fr)`,
                      width: num === 1 ? '12px' : '24px'
                    }}
                  >
                    {Array.from({length: num}).map((_, i) => (
                      <div key={i} className="w-2 h-3 bg-purple-400 rounded-sm"></div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Custom Select Fallback */}
            <select
              value={content?.masonryColumns?.mobile || 1}
              onChange={(e) => updateGallerySetting('masonryColumns', {
                ...content?.masonryColumns,
                mobile: parseInt(e.target.value)
              })}
              className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors bg-white"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
            </select>
            
            <div className="text-center mt-3">
              <span className="text-sm font-medium text-purple-600 bg-purple-100 px-3 py-1 rounded-full">
                Current: {content?.masonryColumns?.mobile || 1} column{(content?.masonryColumns?.mobile || 1) > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Layout Behavior Controls */}
    <div className="bg-white rounded-xl p-6 border border-violet-200 shadow-sm mb-6">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
          <div className="w-4 h-4 bg-white rounded-full relative">
            <div className="absolute inset-1 border border-emerald-500 rounded-full"></div>
          </div>
        </div>
        <h5 className="text-lg font-semibold text-gray-800">Layout Behavior Controls</h5>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Column Fill Strategy */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            Column Fill Strategy
          </label>
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-4 rounded-lg border border-emerald-200">
            <div className="space-y-3">
              {[
                { 
                  value: 'balance', 
                  label: 'Balance', 
                  desc: 'Even distribution across columns'                },
                { 
                  value: 'auto', 
                  label: 'Auto', 
                  desc: 'Browser-controlled distribution'                }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => updateGallerySetting('masonryColumnFill', option.value)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left hover:shadow-md ${
                    (content?.masonryColumnFill || 'balance') === option.value
                      ? 'border-emerald-500 bg-emerald-100 text-emerald-700 shadow-lg ring-2 ring-emerald-200'
                      : 'border-emerald-200 hover:border-emerald-300 bg-white text-emerald-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{option.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h6 className="font-semibold">{option.label}</h6>
                        {(content?.masonryColumnFill || 'balance') === option.value && (
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{option.desc}</p>
                    </div>
                    
                    {/* Visual Fill Preview */}
                    <div className="grid grid-cols-3 gap-0.5 w-12 h-8">
                      {option.value === 'balance' ? (
                        <>
                          <div className="bg-emerald-400 rounded-sm h-6"></div>
                          <div className="bg-emerald-400 rounded-sm h-7"></div>
                          <div className="bg-emerald-400 rounded-sm h-6"></div>
                        </>
                      ) : (
                        <>
                          <div className="bg-emerald-400 rounded-sm h-8"></div>
                          <div className="bg-emerald-400 rounded-sm h-4"></div>
                          <div className="bg-emerald-400 rounded-sm h-6"></div>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Select Fallback */}
            <select
              value={content?.masonryColumnFill || 'balance'}
              onChange={(e) => updateGallerySetting('masonryColumnFill', e.target.value)}
              className="w-full p-3 border-2 border-emerald-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors bg-white mt-3"
            >
              <option value="balance">Balance</option>
              <option value="auto">Auto</option>
            </select>
          </div>
        </div>

        {/* Break Inside Behavior */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            Break Inside Behavior
          </label>
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200">
            <div className="space-y-3">
              {[
                { 
                  value: 'avoid', 
                  label: 'Avoid', 
                  desc: 'Keep items intact'                },
                { 
                  value: 'auto', 
                  label: 'Auto', 
                  desc: 'Allow item breaking'
                },
                { 
                  value: 'avoid-page', 
                  label: 'Avoid Page', 
                  desc: 'Prevent page breaks'
                }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => updateGallerySetting('masonryBreakInside', option.value)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left hover:shadow-md ${
                    (content?.masonryBreakInside || 'avoid') === option.value
                      ? 'border-rose-500 bg-rose-100 text-rose-700 shadow-lg ring-2 ring-rose-200'
                      : 'border-rose-200 hover:border-rose-300 bg-white text-rose-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{option.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h6 className="font-semibold">{option.label}</h6>
                        {(content?.masonryBreakInside || 'avoid') === option.value && (
                          <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{option.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Select Fallback */}
            <select
              value={content?.masonryBreakInside || 'avoid'}
              onChange={(e) => updateGallerySetting('masonryBreakInside', e.target.value)}
              className="w-full p-3 border-2 border-rose-200 rounded-lg focus:border-rose-500 focus:ring-0 transition-colors bg-white mt-3"
            >
              <option value="avoid">Avoid</option>
              <option value="auto">Auto</option>
              <option value="avoid-page">Avoid Page</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    {/* Advanced Masonry Preview */}
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 border-2 border-dashed border-violet-300">
      <div className="text-center mb-6">
        <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm border border-violet-200">
          <div className="w-2 h-2 bg-violet-500 rounded-full animate-pulse"></div>
          <span className="text-gray-700 font-medium">Advanced Masonry Preview</span>
        </div>
      </div>

      {/* Comprehensive Preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Desktop Preview */}
        <div className="bg-white rounded-lg p-4 border border-violet-200 shadow-sm">
          <div className="text-center mb-3">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-700">Desktop</span>
            </div>
            <div className="text-xs text-violet-600 font-medium">
              {content?.masonryColumns?.desktop || 3} columns • {content?.masonryColumnFill || 'balance'} fill
            </div>
          </div>
          <div 
            className="grid gap-1.5 h-24 overflow-hidden"
            style={{ 
              gridTemplateColumns: `repeat(${content?.masonryColumns?.desktop || 3}, 1fr)`
            }}
          >
            {Array.from({length: (content?.masonryColumns?.desktop || 3) * 2}).map((_, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-violet-300 to-purple-400 rounded-sm"
                style={{
                  height: `${Math.random() * 20 + 15}px`
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Mobile Preview */}
        <div className="bg-white rounded-lg p-4 border border-violet-200 shadow-sm">
          <div className="text-center mb-3">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-700">Mobile</span>
            </div>
            <div className="text-xs text-violet-600 font-medium">
              {content?.masonryColumns?.mobile || 1} column{(content?.masonryColumns?.mobile || 1) > 1 ? 's' : ''} • {content?.masonryBreakInside || 'avoid'} break
            </div>
          </div>
          <div 
            className="grid gap-1.5 h-24 overflow-hidden max-w-16 mx-auto"
            style={{ 
              gridTemplateColumns: `repeat(${content?.masonryColumns?.mobile || 1}, 1fr)`
            }}
          >
            {Array.from({length: (content?.masonryColumns?.mobile || 1) * 3}).map((_, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-purple-300 to-pink-400 rounded-sm"
                style={{
                  height: `${Math.random() * 25 + 10}px`
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Behavior Preview */}
        <div className="bg-white rounded-lg p-4 border border-violet-200 shadow-sm">
          <div className="text-center mb-3">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-700">Behavior</span>
            </div>
            <div className="text-xs text-violet-600 font-medium">
              Fill: {content?.masonryColumnFill || 'balance'} • Break: {content?.masonryBreakInside || 'avoid'}
            </div>
          </div>
          <div className="flex justify-center items-center h-16">
            <div className="grid grid-cols-3 gap-1 w-16">
              {content?.masonryColumnFill === 'balance' ? (
                <>
                  <div className="bg-emerald-400 rounded-sm h-8"></div>
                  <div className="bg-emerald-400 rounded-sm h-9"></div>
                  <div className="bg-emerald-400 rounded-sm h-8"></div>
                </>
              ) : (
                <>
                  <div className="bg-emerald-400 rounded-sm h-12"></div>
                  <div className="bg-emerald-400 rounded-sm h-6"></div>
                  <div className="bg-emerald-400 rounded-sm h-9"></div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Settings Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white p-3 rounded-lg shadow-sm border border-violet-200 text-center">
          <div className="text-xs text-gray-600 mb-1">Desktop</div>
          <div className="text-sm font-medium text-violet-600">
            {content?.masonryColumns?.desktop || 3} cols
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow-sm border border-violet-200 text-center">
          <div className="text-xs text-gray-600 mb-1">Mobile</div>
          <div className="text-sm font-medium text-violet-600">
            {content?.masonryColumns?.mobile || 1} col{(content?.masonryColumns?.mobile || 1) > 1 ? 's' : ''}
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow-sm border border-violet-200 text-center">
          <div className="text-xs text-gray-600 mb-1">Fill</div>
          <div className="text-sm font-medium text-violet-600">
            {content?.masonryColumnFill || 'balance'}
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow-sm border border-violet-200 text-center">
          <div className="text-xs text-gray-600 mb-1">Break</div>
          <div className="text-sm font-medium text-violet-600">
            {content?.masonryBreakInside || 'avoid'}
          </div>
        </div>
      </div>

    </div>
  </div>
)}

          {/* Enhanced Layout Controls */}
          {content?.displayType === 'flex' && (
            <div className="border-t pt-4">
              <h5 className="font-medium mb-3">Flex Layout Settings</h5>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Items Per Row (Desktop)</label>
                  <select
                    value={content?.flexItemsPerRow?.desktop || 3}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      updateGallerySetting('flexItemsPerRow', {
                        ...content?.flexItemsPerRow,
                        desktop: newValue
                      });
                    }}
                    className="w-full p-2 border rounded"
                  >
                    <option value={1}>1 Item</option>
                    <option value={2}>2 Items</option>
                    <option value={3}>3 Items</option>
                    <option value={4}>4 Items</option>
                    <option value={5}>5 Items</option>
                    <option value={6}>6 Items</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Items Per Row (Tablet)</label>
                  <select
                    value={content?.flexItemsPerRow?.tablet || 2}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      updateGallerySetting('flexItemsPerRow', {
                        ...content?.flexItemsPerRow,
                        tablet: newValue
                      });
                    }}
                    className="w-full p-2 border rounded"
                  >
                    <option value={1}>1 Item</option>
                    <option value={2}>2 Items</option>
                    <option value={3}>3 Items</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Items Per Row (Mobile)</label>
                  <select
                    value={content?.flexItemsPerRow?.mobile || 1}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      updateGallerySetting('flexItemsPerRow', {
                        ...content?.flexItemsPerRow,
                        mobile: newValue
                      });
                    }}
                    className="w-full p-2 border rounded"
                  >
                    <option value={1}>1 Item</option>
                    <option value={2}>2 Items</option>
                    <option value={3}>3 Items</option>
                  </select>
                </div>
              </div>
            </div>
          )}



          {/* Enhanced Carousel Settings */}
          {content?.displayType === 'carousel' && (
            <div className="mt-6 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-indigo-900">Carousel Configuration</h4>
                  <p className="text-sm text-indigo-700">Customize your carousel behavior, appearance, and navigation</p>
                </div>
              </div>

              {/* Visible Items Section */}
              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Visible Items & Responsiveness</h5>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Desktop Visible Items */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🖥️</span>
                      <label className="text-sm font-medium text-gray-700">Desktop Visible Items</label>
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {[1, 2, 3, 4, 5, 6].map(num => (
                        <button
                          key={`desktop-${num}`}
                          onClick={() => {
                            updateGallerySetting('carouselVisibleItems', {
                              ...content?.carouselVisibleItems,
                              desktop: num
                            });
                          }}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-1 ${(content?.carouselVisibleItems?.desktop || 3) === num
                            ? 'border-cyan-500 bg-cyan-50 text-cyan-700 shadow-md ring-2 ring-cyan-200'
                            : 'border-gray-200 hover:border-cyan-300 text-gray-600 hover:bg-cyan-50'
                            }`}
                        >
                          <div className="text-lg font-bold">{num}</div>
                          <div className="text-xs">Item{num > 1 ? 's' : ''}</div>
                          {/* Visual representation */}
                          <div className="flex gap-0.5">
                            {Array.from({ length: Math.min(num, 4) }).map((_, i) => (
                              <div key={i} className="w-1.5 h-1.5 bg-current opacity-60 rounded-sm"></div>
                            ))}
                            {num > 4 && <div className="text-xs">...</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Mobile Visible Items */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">📱</span>
                      <label className="text-sm font-medium text-gray-700">Mobile Visible Items</label>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[1, 2, 3].map(num => (
                        <button
                          key={`mobile-${num}`}
                          onClick={() => {
                            updateGallerySetting('carouselVisibleItems', {
                              ...content?.carouselVisibleItems,
                              mobile: num
                            });
                          }}
                          className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-2 ${(content?.carouselVisibleItems?.mobile || 1) === num
                            ? 'border-green-500 bg-green-50 text-green-700 shadow-md ring-2 ring-green-200'
                            : 'border-gray-200 hover:border-green-300 text-gray-600 hover:bg-green-50'
                            }`}
                        >
                          <div className="text-xl font-bold">{num}</div>
                          <div className="text-xs">Item{num > 1 ? 's' : ''}</div>
                          {/* Visual representation */}
                          <div className="flex gap-1">
                            {Array.from({ length: num }).map((_, i) => (
                              <div key={i} className="w-2 h-2 bg-current opacity-60 rounded-sm"></div>
                            ))}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Behavior Settings */}
              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Behavior & Performance</h5>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Scroll Behavior */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Scroll Behavior</label>
                    <div className="space-y-2">
                      {[
                        { value: 'smooth', label: 'Smooth', desc: 'Gentle transition' },
                        { value: 'auto', label: 'Auto', desc: 'Browser default' },
                        { value: 'instant', label: 'Instant', desc: 'No animation' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateGallerySetting('carouselScrollBehavior', option.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.carouselScrollBehavior || 'smooth') === option.value
                            ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-md'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                            }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{option.icon}</span>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Scroll Amount */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Scroll Distance</label>
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                      <div className="mb-3">
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {[200, 300, 400, 500].map(amount => (
                            <button
                              key={amount}
                              onClick={() => updateGallerySetting('carouselScrollAmount', amount)}
                              className={`p-2 text-xs font-medium rounded border transition-all ${(content?.carouselScrollAmount || 300) === amount
                                ? 'border-purple-500 bg-purple-100 text-purple-700'
                                : 'border-gray-200 hover:border-purple-300 bg-white'
                                }`}
                            >
                              {amount}px
                            </button>
                          ))}
                        </div>
                      </div>
                      <input
                        type="range"
                        min="100"
                        max="1000"
                        step="50"
                        value={content?.carouselScrollAmount || 300}
                        onChange={(e) => updateGallerySetting('carouselScrollAmount', parseInt(e.target.value))}
                        className="w-full mb-2"
                      />
                      <div className="text-center">
                        <span className="text-sm font-medium text-purple-700">
                          {content?.carouselScrollAmount || 300}px
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Item Width */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Item Width</label>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        {['200px', '256px', '300px', '20vw'].map(width => (
                          <button
                            key={width}
                            onClick={() => updateGallerySetting('carouselItemWidth', width)}
                            className={`p-2 text-xs font-medium rounded border transition-all ${(content?.carouselItemWidth || '256px') === width
                              ? 'border-pink-500 bg-pink-50 text-pink-700'
                              : 'border-gray-200 hover:border-pink-300'
                              }`}
                          >
                            {width}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={content?.carouselItemWidth || '256px'}
                        onChange={(e) => updateGallerySetting('carouselItemWidth', e.target.value)}
                        className="w-full p-3 border-2 border-pink-200 rounded-lg focus:border-pink-500 focus:ring-0 transition-colors"
                        placeholder="e.g., 256px, 20vw"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation & Features */}
              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Navigation & Features</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Feature Toggles */}
                  {[
                    { key: 'carouselCenterMode', label: 'Center Active Item', desc: 'Highlight center item' },
                    { key: 'carouselInfiniteLoop', label: 'Infinite Loop', desc: 'Continuous scrolling' },
                    { key: 'carouselShowDots', label: 'Show Dots Navigation', desc: 'Page indicators' },
                    { key: 'carouselShowCounter', label: 'Show Item Counter', desc: 'Current position' },
                    { key: 'enableScrolling', label: 'Horizontal Scrolling', desc: 'Mouse wheel support', defaultValue: true },
                    { key: 'autoplay', label: 'Enable Autoplay', desc: 'Auto advance slides' }
                  ].map(feature => (
                    <div key={feature.key} className="bg-gradient-to-br from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <div className="relative mt-1">
                          <input
                            type="checkbox"
                            checked={feature.defaultValue !== undefined ?
                              (content?.[feature.key] !== false) :
                              (content?.[feature.key] || false)
                            }
                            onChange={(e) => updateGallerySetting(feature.key, e.target.checked)}
                            className="w-5 h-5 text-emerald-600 border-2 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
                          />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            {((feature.defaultValue !== undefined ?
                              (content?.[feature.key] !== false) :
                              (content?.[feature.key] || false)) && (
                                <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ))}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{feature.icon}</span>
                            <span className="text-sm font-medium text-gray-800">{feature.label}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{feature.desc}</p>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Autoplay Settings */}
              {content?.autoplay && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200 shadow-sm mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h5 className="text-lg font-semibold text-amber-800">⚡ Autoplay Configuration</h5>
                  </div>

                  <div className="space-y-4">
                    <label className="text-sm font-medium text-amber-800">Autoplay Speed (milliseconds)</label>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[1500, 3000, 5000, 8000].map(speed => (
                        <button
                          key={speed}
                          onClick={() => updateGallerySetting('autoplaySpeed', speed)}
                          className={`p-3 text-sm font-medium rounded-lg border-2 transition-all ${(content?.autoplaySpeed || 3000) === speed
                            ? 'border-amber-500 bg-amber-100 text-amber-700 shadow-md'
                            : 'border-amber-200 hover:border-amber-300 bg-white text-amber-700'
                            }`}
                        >
                          <div className="font-bold">{speed / 1000}s</div>
                          <div className="text-xs">
                            {speed === 1500 ? 'Fast' : speed === 3000 ? 'Normal' : speed === 5000 ? 'Slow' : 'Very Slow'}
                          </div>
                        </button>
                      ))}
                    </div>
                    <input
                      type="range"
                      min="1000"
                      max="10000"
                      step="500"
                      value={content?.autoplaySpeed || 3000}
                      onChange={(e) => updateGallerySetting('autoplaySpeed', parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-center">
                      <span className="text-sm font-medium text-amber-700">
                        Current: {(content?.autoplaySpeed || 3000) / 1000}s per slide
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Colors */}
              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Navigation Styling</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Navigation Color</label>
                    <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200">
                      <input
                        type="color"
                        className="w-full h-12 border-2 border-rose-200 rounded-lg cursor-pointer"
                        value={content?.navigationColor || '#e5e7eb'}
                        onChange={(e) => updateGallerySetting('navigationColor', e.target.value)}
                      />
                      <p className="text-xs text-rose-600 mt-2 text-center">
                        Default state: {content?.navigationColor || '#e5e7eb'}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Navigation Hover Color</label>
                    <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-4 rounded-lg border border-pink-200">
                      <input
                        type="color"
                        className="w-full h-12 border-2 border-pink-200 rounded-lg cursor-pointer"
                        value={content?.navigationHoverColor || '#d1d5db'}
                        onChange={(e) => updateGallerySetting('navigationHoverColor', e.target.value)}
                      />
                      <p className="text-xs text-pink-600 mt-2 text-center">
                        Hover state: {content?.navigationHoverColor || '#d1d5db'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Color Preview */}
                <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                  <div className="text-center mb-3">
                    <span className="text-sm font-medium text-gray-600">Navigation Preview</span>
                  </div>
                  <div className="flex justify-center gap-4">
                    <button
                      className="w-10 h-10 rounded-full transition-colors flex items-center justify-center"
                      style={{ backgroundColor: content?.navigationColor || '#e5e7eb' }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      className="w-10 h-10 rounded-full transition-colors flex items-center justify-center"
                      style={{ backgroundColor: content?.navigationHoverColor || '#d1d5db' }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Scrollbar Styling */}
              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-slate-400 to-gray-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">📏 Custom Scrollbar</h5>
                </div>

                {/* Custom Scrollbar Toggle */}
                <div className="mb-6">
                  <label className="flex items-center gap-3 cursor-pointer p-4 bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg border border-slate-200">
                    <input
                      type="checkbox"
                      checked={content?.customScrollbar || false}
                      onChange={(e) => updateGallerySetting('customScrollbar', e.target.checked)}
                      className="w-5 h-5 text-slate-600 border-2 border-gray-300 rounded focus:ring-slate-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-800">Enable Custom Scrollbar</span>
                      <p className="text-xs text-gray-600">Override default browser scrollbar styling</p>
                    </div>
                  </label>
                </div>

                {content?.customScrollbar && (
                  <div className="space-y-6">
                    {/* Height Setting */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700">Scrollbar Height</label>
                        <div className="space-y-2">
                          {['6px', '8px', '12px', '16px'].map(height => (
                            <button
                              key={height}
                              onClick={() => updateGallerySetting('scrollbarHeight', height)}
                              className={`w-full p-3 text-sm font-medium rounded-lg border-2 transition-all ${(content?.scrollbarHeight || '8px') === height
                                ? 'border-slate-500 bg-slate-50 text-slate-700'
                                : 'border-gray-200 hover:border-slate-300'
                                }`}
                            >
                              {height}
                            </button>
                          ))}
                          <input
                            type="text"
                            value={content?.scrollbarHeight || '8px'}
                            onChange={(e) => updateGallerySetting('scrollbarHeight', e.target.value)}
                            className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-slate-500 focus:ring-0"
                            placeholder="Custom height"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700">Thumb Border Radius</label>
                        <div className="space-y-2">
                          {['0px', '4px', '8px', '10px'].map(radius => (
                            <button
                              key={radius}
                              onClick={() => updateGallerySetting('scrollbarThumbRadius', radius)}
                              className={`w-full p-3 text-sm font-medium rounded-lg border-2 transition-all ${(content?.scrollbarThumbRadius || '10px') === radius
                                ? 'border-slate-500 bg-slate-50 text-slate-700'
                                : 'border-gray-200 hover:border-slate-300'
                                }`}
                            >
                              {radius} {radius === '0px' ? '(Square)' : radius === '10px' ? '(Rounded)' : ''}
                            </button>
                          ))}
                          <input
                            type="text"
                            value={content?.scrollbarThumbRadius || '10px'}
                            onChange={(e) => updateGallerySetting('scrollbarThumbRadius', e.target.value)}
                            className="w-full p-3 border-2 border-slate-200 rounded-lg focus:border-slate-500 focus:ring-0"
                            placeholder="Custom radius"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Color Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700">Track Color</label>
                        <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                          <input
                            type="color"
                            value={content?.scrollbarTrackColor || '#f1f1f1'}
                            onChange={(e) => updateGallerySetting('scrollbarTrackColor', e.target.value)}
                            className="w-full h-12 border-2 border-gray-200 rounded-lg cursor-pointer"
                          />
                          <p className="text-xs text-gray-600 mt-2 text-center">
                            Track: {content?.scrollbarTrackColor || '#f1f1f1'}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700">Thumb Color</label>
                        <div className="bg-gradient-to-br from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
                          <input
                            type="color"
                            value={content?.scrollbarThumbColor || '#c1c1c1'}
                            onChange={(e) => updateGallerySetting('scrollbarThumbColor', e.target.value)}
                            className="w-full h-12 border-2 border-slate-200 rounded-lg cursor-pointer"
                          />
                          <p className="text-xs text-slate-600 mt-2 text-center">
                            Thumb: {content?.scrollbarThumbColor || '#c1c1c1'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Scrollbar Preview */}
                    <div className="mt-6 p-6 bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg border-2 border-dashed border-gray-300">
                      <div className="text-center mb-4">
                        <span className="text-sm font-medium text-gray-600">Scrollbar Preview</span>
                      </div>
                      <div className="relative">
                        <div
                          className="w-full bg-gray-200 rounded-lg overflow-x-auto"
                          style={{
                            height: content?.scrollbarHeight || '8px',
                            backgroundColor: content?.scrollbarTrackColor || '#f1f1f1'
                          }}
                        >
                          <div
                            className="h-full rounded-lg"
                            style={{
                              width: '40%',
                              backgroundColor: content?.scrollbarThumbColor || '#c1c1c1',
                              borderRadius: content?.scrollbarThumbRadius || '10px'
                            }}
                          ></div>
                        </div>
                        <p className="text-xs text-center text-gray-500 mt-2">
                          Custom scrollbar with {content?.scrollbarHeight || '8px'} height
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Carousel Preview */}
              <div className="mt-8 p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border-2 border-dashed border-indigo-300">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm border border-indigo-200">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                    <span className="text-gray-700 font-medium">Carousel Preview</span>
                  </div>
                </div>

                {/* Settings Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-indigo-200">
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-700">Desktop</div>
                      <div className="text-lg font-bold text-indigo-600">
                        {content?.carouselVisibleItems?.desktop || 3} items
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm border border-indigo-200">
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-700">Mobile</div>
                      <div className="text-lg font-bold text-green-600">
                        {content?.carouselVisibleItems?.mobile || 1} item{(content?.carouselVisibleItems?.mobile || 1) > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm border border-indigo-200">
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-700">Behavior</div>
                      <div className="text-sm font-bold text-purple-600">
                        {content?.carouselScrollBehavior || 'smooth'}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm border border-indigo-200">
                    <div className="text-center">

                      <div className="text-sm font-medium text-gray-700">Autoplay</div>
                      <div className="text-sm font-bold text-amber-600">
                        {content?.autoplay ? `${(content?.autoplaySpeed || 3000) / 1000}s` : 'Off'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Visual Carousel Mockup */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-indigo-200">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: content?.navigationColor || '#e5e7eb' }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    <div className="flex-1 mx-4">
                      <div
                        className="flex gap-3 overflow-hidden justify-center"
                        style={{
                          maxWidth: `${(content?.carouselVisibleItems?.desktop || 3) * 100}px`
                        }}
                      >
                        {Array.from({ length: content?.carouselVisibleItems?.desktop || 3 }).map((_, i) => (
                          <div
                            key={i}
                            className={`bg-gradient-to-br from-indigo-300 to-purple-400 rounded-lg flex items-center justify-center text-white font-semibold shadow-md ${content?.carouselCenterMode && i === Math.floor((content?.carouselVisibleItems?.desktop || 3) / 2)
                              ? 'ring-2 ring-yellow-400 ring-offset-2'
                              : ''
                              }`}
                            style={{
                              width: '80px',
                              height: '60px'
                            }}
                          >
                            {i + 1}
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: content?.navigationHoverColor || '#d1d5db' }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Navigation Elements */}
                  <div className="flex justify-center items-center gap-4">
                    {content?.carouselShowDots && (
                      <div className="flex gap-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div
                            key={i}
                            className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-indigo-500' : 'bg-gray-300'
                              }`}
                          ></div>
                        ))}
                      </div>
                    )}

                    {content?.carouselShowCounter && (
                      <div className="bg-gray-100 px-3 py-1 rounded-full text-xs font-medium text-gray-600">
                        1 / 5
                      </div>
                    )}

                    {content?.autoplay && (
                      <div className="flex items-center gap-1 text-xs text-amber-600">
                        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                        Auto
                      </div>
                    )}
                  </div>

                  {/* Custom Scrollbar Preview */}
                  {content?.customScrollbar && (
                    <div className="mt-4">
                      <div
                        className="w-full mx-auto rounded-lg"
                        style={{
                          height: content?.scrollbarHeight || '8px',
                          backgroundColor: content?.scrollbarTrackColor || '#f1f1f1',
                          maxWidth: '300px'
                        }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: '30%',
                            backgroundColor: content?.scrollbarThumbColor || '#c1c1c1',
                            borderRadius: content?.scrollbarThumbRadius || '10px'
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-center mt-4">
                  <p className="text-xs text-indigo-600">
                    Preview shows desktop layout with current settings
                  </p>
                </div>
              </div>


            </div>
          )}


          {/* Enhanced Advanced Scrolling & Performance */}
          {content?.displayType === 'carousel' && (
            <div className="mt-6 bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">

                <div>
                  <h4 className="text-xl font-bold text-slate-800">Advanced Scrolling & Performance</h4>
                  <p className="text-sm text-slate-600">Fine-tune scroll behavior, performance, and advanced features</p>
                </div>
              </div>

              {/* Scroll Snap Configuration */}
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Scroll Snap Configuration</h5>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Scroll Snap Type */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700">Scroll Snap Type</label>
                    <div className="space-y-2">
                      {[
                        { value: 'none', label: 'None', desc: 'No snapping' },
                        { value: 'x mandatory', label: 'X Mandatory', desc: 'Always snap horizontally' },
                        { value: 'x proximity', label: 'X Proximity', desc: 'Snap when close' },
                        { value: 'both mandatory', label: 'Both Mandatory', desc: 'Snap both directions' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateGallerySetting('scrollSnapType', option.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.scrollSnapType || 'x mandatory') === option.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{option.icon}</span>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Scroll Snap Align */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700">Scroll Snap Alignment</label>
                    <div className="space-y-2">
                      {[
                        { value: 'start', label: 'Start', desc: 'Align to beginning' },
                        { value: 'center', label: 'Center', desc: 'Align to center' },
                        { value: 'end', label: 'End', desc: 'Align to end' },
                        { value: 'none', label: 'None', desc: 'No alignment' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateGallerySetting('scrollSnapAlign', option.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.scrollSnapAlign || 'start') === option.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-md ring-2 ring-indigo-200'
                            : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{option.icon}</span>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Scroll Behavior */}
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Advanced Scroll Behavior</h5>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Overscroll Behavior */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700">Overscroll Behavior</label>
                    <div className="space-y-2">
                      {[
                        { value: 'auto', label: 'Auto', desc: 'Default browser behavior' },
                        { value: 'contain', label: 'Contain', desc: 'Keep scroll within container' },
                        { value: 'none', label: 'None', desc: 'Disable overscroll effects' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateGallerySetting('overscrollBehavior', option.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.overscrollBehavior || 'auto') === option.value
                            ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-md ring-2 ring-purple-200'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{option.icon}</span>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Momentum Scrolling */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700">Momentum Scrolling</label>
                    <div className="space-y-2">
                      {[
                        { value: 'auto', label: 'Auto', desc: 'Browser default' },
                        { value: 'touch', label: 'Touch', desc: 'Enhanced touch scrolling' },
                        { value: 'none', label: 'None', desc: 'Disable momentum' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateGallerySetting('momentumScrolling', option.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.momentumScrolling || 'touch') === option.value
                            ? 'border-pink-500 bg-pink-50 text-pink-700 shadow-md ring-2 ring-pink-200'
                            : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{option.icon}</span>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Features */}
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Advanced Features</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Feature Toggles */}
                  {[
                    { key: 'enableVirtualScrolling', label: 'Virtual Scrolling', desc: 'Optimize large lists' },
                    { key: 'enableScrollIndicators', label: 'Scroll Indicators', desc: 'Show scroll position' }
                  ].map(feature => (
                    <div key={feature.key} className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <div className="relative mt-1">
                          <input
                            type="checkbox"
                            checked={content?.[feature.key] || false}
                            onChange={(e) => updateGallerySetting(feature.key, e.target.checked)}
                            className="w-5 h-5 text-emerald-600 border-2 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
                          />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            {content?.[feature.key] && (
                              <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{feature.icon}</span>
                            <span className="text-sm font-medium text-gray-800">{feature.label}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{feature.desc}</p>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scroll Indicators Configuration */}
              {content?.enableScrollIndicators && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 shadow-sm mb-6">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h5 className="text-lg font-semibold text-blue-800">Scroll Indicators Configuration</h5>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Indicator Style */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-blue-800">Indicator Style</label>
                      <div className="space-y-2">
                        {[
                          { value: 'dots', label: 'Dots', desc: 'Round indicators' },
                          { value: 'lines', label: 'Lines', desc: 'Linear indicators' },
                          { value: 'progress', label: 'Progress Bar', desc: 'Continuous bar' },
                          { value: 'thumbnails', label: 'Thumbnails', desc: 'Mini previews' }
                        ].map(option => (
                          <button
                            key={option.value}
                            onClick={() => updateGallerySetting('scrollIndicatorStyle', option.value)}
                            className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.scrollIndicatorStyle || 'dots') === option.value
                              ? 'border-blue-500 bg-blue-100 text-blue-700 shadow-md'
                              : 'border-blue-200 hover:border-blue-300 bg-white hover:bg-blue-50'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{option.icon}</span>
                              <div>
                                <div className="text-sm font-medium">{option.label}</div>
                                <div className="text-xs text-gray-500">{option.desc}</div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Indicator Position */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-blue-800">Position</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: 'top', label: 'Top', icon: '⬆️' },
                          { value: 'bottom', label: 'Bottom', icon: '⬇️' },
                          { value: 'left', label: 'Left', icon: '⬅️' },
                          { value: 'right', label: 'Right', icon: '➡️' }
                        ].map(option => (
                          <button
                            key={option.value}
                            onClick={() => updateGallerySetting('scrollIndicatorPosition', option.value)}
                            className={`p-3 rounded-lg border-2 transition-all text-center ${(content?.scrollIndicatorPosition || 'bottom') === option.value
                              ? 'border-blue-500 bg-blue-100 text-blue-700'
                              : 'border-blue-200 hover:border-blue-300 bg-white'
                              }`}
                          >
                            <div className="text-lg">{option.icon}</div>
                            <div className="text-xs font-medium">{option.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Indicator Color */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-blue-800">Indicator Color</label>
                      <div className="bg-white p-4 rounded-lg border border-blue-200">
                        <input
                          type="color"
                          value={content?.scrollIndicatorColor || '#3b82f6'}
                          onChange={(e) => updateGallerySetting('scrollIndicatorColor', e.target.value)}
                          className="w-full h-12 border-2 border-blue-200 rounded-lg cursor-pointer"
                        />
                        <p className="text-xs text-blue-600 mt-2 text-center">
                          {content?.scrollIndicatorColor || '#3b82f6'}
                        </p>

                        {/* Indicator Preview */}
                        <div className="mt-4 flex justify-center">
                          <div className="flex gap-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <div
                                key={i}
                                className={`rounded-full transition-all ${content?.scrollIndicatorStyle === 'lines' ? 'w-4 h-1' : 'w-2 h-2'
                                  }`}
                                style={{
                                  backgroundColor: i === 2 ? (content?.scrollIndicatorColor || '#3b82f6') : '#e5e7eb'
                                }}
                              ></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Performance Optimization */}
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Performance Optimization</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Performance Toggles */}
                  {/* {[
          { key: 'enableGPUAcceleration', label: 'GPU Acceleration',  desc: 'Hardware acceleration', defaultValue: true },
          { key: 'enableWillChange', label: 'Will-Change', desc: 'CSS optimization', defaultValue: true }
        ].map(feature => (
          <div key={feature.key} className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <div className="relative mt-1">
                <input
                  type="checkbox"
                  checked={feature.defaultValue !== undefined ? 
                    (content?.[feature.key] !== false) : 
                    (content?.[feature.key] || false)
                  }
                  onChange={(e) => updateGallerySetting(feature.key, e.target.checked)}
                  className="w-5 h-5 text-orange-600 border-2 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {((feature.defaultValue !== undefined ? 
                      (content?.[feature.key] !== false) : 
                      (content?.[feature.key] || false)) && (
                    <svg className="w-3 h-3 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{feature.icon}</span>
                  <span className="text-sm font-medium text-gray-800">{feature.label}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{feature.desc}</p>
              </div>
            </label>
          </div>
        ))} */}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Rendering Optimization */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Rendering Optimization</label>
                    <div className="space-y-2">
                      {[
                        { value: 'auto', label: 'Auto', desc: 'Browser decides' },
                        { value: 'speed', label: 'Speed', desc: 'Prioritize performance' },
                        { value: 'quality', label: 'Quality', desc: 'Prioritize quality' },
                        { value: 'crisp-edges', label: 'Crisp Edges', desc: 'Sharp pixel rendering' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateGallerySetting('renderingOptimization', option.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.renderingOptimization || 'auto') === option.value
                            ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md ring-2 ring-orange-200'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{option.icon}</span>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Scroll Performance */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Scroll Performance</label>
                    <div className="space-y-2">
                      {[
                        { value: 'auto', label: 'Auto', desc: 'Default behavior' },
                        { value: 'passive', label: 'Passive', desc: 'Non-blocking scrolling' },
                        { value: 'active', label: 'Active', desc: 'Interactive scrolling' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateGallerySetting('scrollPerformance', option.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.scrollPerformance || 'auto') === option.value
                            ? 'border-red-500 bg-red-50 text-red-700 shadow-md ring-2 ring-red-200'
                            : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{option.icon}</span>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.desc}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Scroll Behaviors */}
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <h5 className="text-lg font-semibold text-gray-800">Advanced Scroll Behaviors</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Behavior Toggles */}
                  {/* {[
          { key: 'enableScrollMagnetism', label: 'Scroll Magnetism',  desc: 'Magnetic snap effect' },
          { key: 'enableScrollPaging', label: 'Page Scrolling', desc: 'Page-by-page movement' }
        ].map(feature => (
          <div key={feature.key} className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-lg border border-violet-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <div className="relative mt-1">
                <input
                  type="checkbox"
                  checked={content?.[feature.key] || false}
                  onChange={(e) => updateGallerySetting(feature.key, e.target.checked)}
                  className="w-5 h-5 text-violet-600 border-2 border-gray-300 rounded focus:ring-violet-500 focus:ring-2"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {content?.[feature.key] && (
                    <svg className="w-3 h-3 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{feature.icon}</span>
                  <span className="text-sm font-medium text-gray-800">{feature.label}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{feature.desc}</p>
              </div>
            </label>
          </div>
        ))} */}
                </div>

                {/* Scroll Physics Controls */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Scroll Inertia */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700"> Scroll Inertia</label>
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-lg border border-violet-200">
                      <div className="mb-3">
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          {[
                            { value: 0.5, label: 'Low' },
                            { value: 0.9, label: 'Normal' },
                            { value: 1.0, label: 'High' }
                          ].map(preset => (
                            <button
                              key={preset.value}
                              onClick={() => updateGallerySetting('scrollInertia', preset.value)}
                              className={`p-2 text-xs font-medium rounded border transition-all ${(content?.scrollInertia || 0.9) === preset.value
                                ? 'border-violet-500 bg-violet-100 text-violet-700'
                                : 'border-gray-200 hover:border-violet-300 bg-white'
                                }`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={content?.scrollInertia || 0.9}
                        onChange={(e) => updateGallerySetting('scrollInertia', parseFloat(e.target.value))}
                        className="w-full mb-2 accent-violet-500"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>No inertia</span>
                        <span className="font-medium text-violet-600">
                          {content?.scrollInertia || 0.9}
                        </span>
                        <span>Maximum</span>
                      </div>
                      <p className="text-xs text-violet-600 mt-2 text-center">
                        Controls how long scrolling continues after release
                      </p>
                    </div>
                  </div>

                  {/* Elastic Bounce */}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700"> Elastic Bounce</label>
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                      <div className="mb-3">
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          {[
                            { value: 0.1, label: 'Subtle' },
                            { value: 0.3, label: 'Normal' },
                            { value: 0.7, label: 'Bouncy' }
                          ].map(preset => (
                            <button
                              key={preset.value}
                              onClick={() => updateGallerySetting('elasticBounce', preset.value)}
                              className={`p-2 text-xs font-medium rounded border transition-all ${(content?.elasticBounce || 0.3) === preset.value
                                ? 'border-purple-500 bg-purple-100 text-purple-700'
                                : 'border-gray-200 hover:border-purple-300 bg-white'
                                }`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={content?.elasticBounce || 0.3}
                        onChange={(e) => updateGallerySetting('elasticBounce', parseFloat(e.target.value))}
                        className="w-full mb-2 accent-purple-500"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>No bounce</span>
                        <span className="font-medium text-purple-600">
                          {content?.elasticBounce || 0.3}
                        </span>
                        <span>Very bouncy</span>
                      </div>
                      <p className="text-xs text-purple-600 mt-2 text-center">
                        Bounce effect when reaching scroll boundaries
                      </p>
                    </div>
                  </div>
                </div>

                {/* Physics Preview */}
                <div className="mt-6 p-4 bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg border-2 border-dashed border-slate-300">
                  <div className="text-center mb-4">
                    <span className="text-sm font-medium text-gray-600">Physics Preview</span>
                  </div>
                  <div className="flex justify-center items-center gap-6">
                    <div className="text-center">
                      <div className="w-16 h-4 bg-gradient-to-r from-violet-300 to-purple-400 rounded-full mb-2 relative overflow-hidden">
                        <div
                          className="absolute top-0 left-0 h-full bg-violet-500 rounded-full transition-all duration-1000"
                          style={{ width: `${(content?.scrollInertia || 0.9) * 100}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-violet-600 font-medium">
                        Inertia: {content?.scrollInertia || 0.9}
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="w-16 h-4 bg-gradient-to-r from-purple-300 to-pink-400 rounded-full mb-2 relative overflow-hidden">
                        <div
                          className="absolute top-0 left-0 h-full bg-purple-500 rounded-full transition-all duration-1000"
                          style={{ width: `${(content?.elasticBounce || 0.3) * 100}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-purple-600 font-medium">
                        Bounce: {content?.elasticBounce || 0.3}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-center text-gray-500 mt-3">
                    Visual representation of current physics settings
                  </p>
                </div>
              </div>



            </div>
          )}



          {/* Enhanced Touch & Gesture Controls */}
          <div className="mt-6 bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
              </div>
              <div>
                <h4 className="text-xl font-bold text-cyan-900"> Touch & Gesture Controls</h4>
                <p className="text-sm text-cyan-700">Configure touch interactions, gestures, and mobile navigation</p>
              </div>
            </div>

            {/* Touch Features */}
            <div className="bg-white rounded-xl p-6 border border-cyan-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h5 className="text-lg font-semibold text-gray-800"> Touch Features</h5>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Touch Gesture Features */}
                {[
                  {
                    key: 'enableTouchGestures',
                    label: 'Touch Gestures',
                    desc: 'Enable touch and swipe interactions',
                    defaultValue: true,
                    color: 'emerald'
                  },
                  {
                    key: 'enablePinchZoom',
                    label: 'Pinch to Zoom',
                    desc: 'Zoom in/out with pinch gesture',
                    defaultValue: false,
                    color: 'blue'
                  },
                  {
                    key: 'enableSwipeNavigation',
                    label: 'Swipe Navigation',
                    desc: 'Navigate by swiping left/right',
                    defaultValue: true,
                    color: 'purple'
                  },
                  {
                    key: 'enableDoubleTapZoom',
                    label: 'Double Tap Zoom',
                    desc: 'Zoom with double tap gesture',
                    defaultValue: false,
                    color: 'pink'
                  }
                ].map(feature => (
                  <div key={feature.key} className={`bg-gradient-to-br from-${feature.color}-50 to-${feature.color}-100 p-5 rounded-xl border border-${feature.color}-200 shadow-sm`}>
                    <label className="flex items-start gap-4 cursor-pointer">
                      <div className="relative mt-1">
                        <input
                          type="checkbox"
                          checked={feature.defaultValue !== undefined ?
                            (content?.[feature.key] !== false) :
                            (content?.[feature.key] || false)
                          }
                          onChange={(e) => updateGallerySetting(feature.key, e.target.checked)}
                          className={`w-6 h-6 text-${feature.color}-600 border-2 border-gray-300 rounded-lg focus:ring-${feature.color}-500 focus:ring-2 transition-all`}
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          {((feature.defaultValue !== undefined ?
                            (content?.[feature.key] !== false) :
                            (content?.[feature.key] || false)) && (
                              <svg className={`w-4 h-4 text-${feature.color}-600`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ))}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl">{feature.icon}</span>
                          <div>
                            <span className={`text-lg font-semibold text-${feature.color}-800`}>{feature.label}</span>
                            {((feature.defaultValue !== undefined ?
                              (content?.[feature.key] !== false) :
                              (content?.[feature.key] || false)) && (
                                <div className={`inline-block ml-2 w-2 h-2 bg-${feature.color}-500 rounded-full animate-pulse`}></div>
                              ))}
                          </div>
                        </div>
                        <p className={`text-sm text-${feature.color}-700`}>{feature.desc}</p>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Sensitivity & Thresholds */}
            <div className="bg-white rounded-xl p-6 border border-cyan-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Sensitivity & Timing</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Swipe Sensitivity */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Swipe Sensitivity
                  </label>
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                    <div className="mb-3">
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {[
                          { value: 0.5, label: 'Low', desc: 'Less sensitive' },
                          { value: 1.0, label: 'Normal', desc: 'Balanced' },
                          { value: 1.5, label: 'High', desc: 'More sensitive' }
                        ].map(preset => (
                          <button
                            key={preset.value}
                            onClick={() => updateGallerySetting('swipeSensitivity', preset.value)}
                            className={`p-3 text-xs font-medium rounded-lg border-2 transition-all text-center ${(content?.swipeSensitivity || 1) === preset.value
                              ? 'border-orange-500 bg-orange-100 text-orange-700 shadow-md'
                              : 'border-orange-200 hover:border-orange-300 bg-white text-orange-700'
                              }`}
                          >
                            <div className="font-bold text-sm">{preset.label}</div>
                            <div className="text-xs text-gray-500">{preset.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="2"
                      step="0.1"
                      value={content?.swipeSensitivity || 1}
                      onChange={(e) => updateGallerySetting('swipeSensitivity', parseFloat(e.target.value))}
                      className="w-full mb-3 accent-orange-500"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>0.1x</span>
                      <span className="font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded">
                        {content?.swipeSensitivity || 1}x
                      </span>
                      <span>2x</span>
                    </div>
                    <p className="text-xs text-orange-600 mt-2 text-center">
                      Higher values = more responsive to small movements
                    </p>
                  </div>
                </div>

                {/* Touch Threshold */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Touch Threshold
                  </label>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                    <div className="mb-3">
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {[
                          { value: 15, label: 'Tight', desc: '15px' },
                          { value: 30, label: 'Normal', desc: '30px' },
                          { value: 50, label: 'Loose', desc: '50px' }
                        ].map(preset => (
                          <button
                            key={preset.value}
                            onClick={() => updateGallerySetting('touchThreshold', preset.value)}
                            className={`p-3 text-xs font-medium rounded-lg border-2 transition-all text-center ${(content?.touchThreshold || 30) === preset.value
                              ? 'border-blue-500 bg-blue-100 text-blue-700 shadow-md'
                              : 'border-blue-200 hover:border-blue-300 bg-white text-blue-700'
                              }`}
                          >
                            <div className="font-bold text-sm">{preset.label}</div>
                            <div className="text-xs text-gray-500">{preset.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="number"
                      min="10"
                      max="100"
                      value={content?.touchThreshold || 30}
                      onChange={(e) => updateGallerySetting('touchThreshold', parseInt(e.target.value))}
                      className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors text-center font-medium"
                    />
                    <div className="text-center mt-2">
                      <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                        {content?.touchThreshold || 30} pixels
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-2 text-center">
                      Minimum distance to register a swipe
                    </p>
                  </div>
                </div>

                {/* Gesture Timeout */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Gesture Timeout
                  </label>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                    <div className="mb-3">
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {[
                          { value: 200, label: 'Fast', desc: '0.2s' },
                          { value: 300, label: 'Normal', desc: '0.3s' },
                          { value: 500, label: 'Slow', desc: '0.5s' }
                        ].map(preset => (
                          <button
                            key={preset.value}
                            onClick={() => updateGallerySetting('gestureTimeout', preset.value)}
                            className={`p-3 text-xs font-medium rounded-lg border-2 transition-all text-center ${(content?.gestureTimeout || 300) === preset.value
                              ? 'border-purple-500 bg-purple-100 text-purple-700 shadow-md'
                              : 'border-purple-200 hover:border-purple-300 bg-white text-purple-700'
                              }`}
                          >
                            <div className="font-bold text-sm">{preset.label}</div>
                            <div className="text-xs text-gray-500">{preset.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="number"
                      min="100"
                      max="2000"
                      step="100"
                      value={content?.gestureTimeout || 300}
                      onChange={(e) => updateGallerySetting('gestureTimeout', parseInt(e.target.value))}
                      className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors text-center font-medium"
                    />
                    <div className="text-center mt-2">
                      <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded">
                        {content?.gestureTimeout || 300}ms
                      </span>
                    </div>
                    <p className="text-xs text-purple-600 mt-2 text-center">
                      Maximum time to complete a gesture
                    </p>
                  </div>
                </div>
              </div>
            </div>


          </div>

          {/* Enhanced Container Styling */}
          <div className="mt-6 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                </svg>
              </div>
              <div>
                <h4 className="text-xl font-bold text-rose-900">Container Styling</h4>
                <p className="text-sm text-rose-700">Customize the appearance and layout of your gallery container</p>
              </div>
            </div>

            {/* Colors Section */}
            <div className="bg-white rounded-xl p-6 border border-rose-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-violet-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                  </svg>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Color Scheme</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Background Color */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Background Color
                  </label>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                    {/* Color Presets */}
                    <div className="grid grid-cols-6 gap-2 mb-4">
                      {[
                        { color: '#ffffff', label: 'White' },
                        { color: '#f8fafc', label: 'Light Gray' },
                        { color: '#f1f5f9', label: 'Slate' },
                        { color: '#f0f9ff', label: 'Sky' },
                        { color: '#f0fdf4', label: 'Green' },
                        { color: '#fefce8', label: 'Yellow' }
                      ].map(preset => (
                        <button
                          key={preset.color}
                          onClick={() => updateGallerySetting('backgroundColor', preset.color)}
                          className={`w-10 h-10 rounded-lg border-2 transition-all shadow-sm hover:shadow-md ${(content?.backgroundColor || '#ffffff') === preset.color
                            ? 'border-purple-500 ring-2 ring-purple-200 scale-110'
                            : 'border-gray-300 hover:border-purple-300'
                            }`}
                          style={{ backgroundColor: preset.color }}
                          title={preset.label}
                        >
                          {(content?.backgroundColor || '#ffffff') === preset.color && (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Custom Color Picker */}
                    <input
                      type="color"
                      className="w-full h-12 border-2 border-purple-200 rounded-lg cursor-pointer"
                      value={content?.backgroundColor || '#ffffff'}
                      onChange={(e) => updateGallerySetting('backgroundColor', e.target.value)}
                    />
                    <p className="text-xs text-purple-600 mt-2 text-center font-medium">
                      Current: {content?.backgroundColor || '#ffffff'}
                    </p>
                  </div>
                </div>

                {/* Border Color */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Border Color
                  </label>
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-lg border border-violet-200">
                    {/* Border Color Presets */}
                    <div className="grid grid-cols-6 gap-2 mb-4">
                      {[
                        { color: '#e5e7eb', label: 'Light Gray' },
                        { color: '#d1d5db', label: 'Gray' },
                        { color: '#9ca3af', label: 'Dark Gray' },
                        { color: '#6366f1', label: 'Indigo' },
                        { color: '#8b5cf6', label: 'Violet' },
                        { color: '#ec4899', label: 'Pink' }
                      ].map(preset => (
                        <button
                          key={preset.color}
                          onClick={() => updateGallerySetting('borderColor', preset.color)}
                          className={`w-10 h-10 rounded-lg border-4 transition-all shadow-sm hover:shadow-md ${(content?.borderColor || '#e5e7eb') === preset.color
                            ? 'ring-2 ring-violet-400 scale-110'
                            : 'hover:ring-1 hover:ring-violet-300'
                            }`}
                          style={{ borderColor: preset.color, backgroundColor: 'white' }}
                          title={preset.label}
                        >
                          {(content?.borderColor || '#e5e7eb') === preset.color && (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Custom Border Color Picker */}
                    <input
                      type="color"
                      className="w-full h-12 border-2 border-violet-200 rounded-lg cursor-pointer"
                      value={content?.borderColor || '#e5e7eb'}
                      onChange={(e) => updateGallerySetting('borderColor', e.target.value)}
                    />
                    <p className="text-xs text-violet-600 mt-2 text-center font-medium">
                      Current: {content?.borderColor || '#e5e7eb'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Border & Effects Section */}
            <div className="bg-white rounded-xl p-6 border border-rose-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Border & Visual Effects</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Border Width */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Border Width
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: '0', label: 'None', preview: 'No border' },
                      { value: '1', label: '1px', preview: 'Thin line' },
                      { value: '2', label: '2px', preview: 'Medium line' },
                      { value: '4', label: '4px', preview: 'Thick line' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => updateGallerySetting('borderWidth', option.value)}
                        className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.borderWidth || '0') === option.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-xs text-gray-500">{option.preview}</div>
                          </div>
                          <div
                            className="w-8 h-6 bg-white rounded border-gray-400"
                            style={{ borderWidth: `${option.value}px` }}
                          ></div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Border Radius */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Border Radius
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'none', label: 'None', radius: '0px', preview: 'Square corners' },
                      { value: 'rounded-sm', label: 'Small', radius: '2px', preview: 'Slightly rounded' },
                      { value: 'rounded', label: 'Medium', radius: '4px', preview: 'Rounded corners' },
                      { value: 'rounded-lg', label: 'Large', radius: '8px', preview: 'More rounded' },
                      { value: 'rounded-xl', label: 'Extra Large', radius: '12px', preview: 'Very rounded' },
                      { value: 'rounded-full', label: 'Full', radius: '50%', preview: 'Pill shape' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => updateGallerySetting('borderRadius', option.value)}
                        className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.borderRadius || 'rounded') === option.value
                          ? 'border-cyan-500 bg-cyan-50 text-cyan-700 shadow-md ring-2 ring-cyan-200'
                          : 'border-gray-200 hover:border-cyan-300 hover:bg-cyan-50'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-xs text-gray-500">{option.preview}</div>
                          </div>
                          <div
                            className="w-8 h-6 bg-cyan-200 border border-cyan-400"
                            style={{ borderRadius: option.radius }}
                          ></div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Box Shadow */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Box Shadow
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'none', label: 'None', preview: 'No shadow', shadow: 'none' },
                      { value: 'small', label: 'Small', preview: 'Subtle depth', shadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
                      { value: 'medium', label: 'Medium', preview: 'Moderate depth', shadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' },
                      { value: 'large', label: 'Large', preview: 'Strong depth', shadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' },
                      { value: 'xl', label: 'Extra Large', preview: 'Maximum depth', shadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => updateGallerySetting('boxShadow', option.value)}
                        className={`w-full p-3 rounded-lg border-2 transition-all text-left ${(content?.boxShadow || 'none') === option.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-md ring-2 ring-indigo-200'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-xs text-gray-500">{option.preview}</div>
                          </div>
                          <div
                            className="w-8 h-6 bg-white border border-gray-200 rounded"
                            style={{ boxShadow: option.shadow }}
                          ></div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Dimensions Section */}
            <div className="bg-white rounded-xl p-6 border border-rose-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Container Dimensions</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Container Padding */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Container Padding
                  </label>
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-4 rounded-lg border border-emerald-200">
                    {/* Padding Presets */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {[
                        { value: '0', label: 'None' },
                        { value: '0.5rem', label: 'XS' },
                        { value: '1rem', label: 'SM' },
                        { value: '1.5rem', label: 'MD' },
                        { value: '2rem', label: 'LG' },
                        { value: '3rem', label: 'XL' }
                      ].map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('containerPadding', preset.value)}
                          className={`p-2 text-xs font-medium rounded border-2 transition-all ${(content?.containerPadding || '') === preset.value
                            ? 'border-emerald-500 bg-emerald-100 text-emerald-700'
                            : 'border-emerald-200 hover:border-emerald-300 bg-white text-emerald-700'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={content?.containerPadding || ''}
                      onChange={(e) => updateGallerySetting('containerPadding', e.target.value)}
                      className="w-full p-3 border-2 border-emerald-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors"
                      placeholder="e.g., 1rem, 16px"
                    />
                    <p className="text-xs text-emerald-600 mt-2 text-center">
                      Inner spacing around content
                    </p>
                  </div>
                </div>

                {/* Max Width */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Max Width
                  </label>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                    {/* Width Presets */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {[
                        { value: '100%', label: 'Full' },
                        { value: '1200px', label: '1200px' },
                        { value: '800px', label: '800px' },
                        { value: '600px', label: '600px' },
                        { value: '90vw', label: '90vw' },
                        { value: 'none', label: 'None' }
                      ].map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('maxWidth', preset.value)}
                          className={`p-2 text-xs font-medium rounded border-2 transition-all ${(content?.maxWidth || '') === preset.value
                            ? 'border-blue-500 bg-blue-100 text-blue-700'
                            : 'border-blue-200 hover:border-blue-300 bg-white text-blue-700'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={content?.maxWidth || ''}
                      onChange={(e) => updateGallerySetting('maxWidth', e.target.value)}
                      className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                      placeholder="e.g., 1200px, 100%"
                    />
                    <p className="text-xs text-blue-600 mt-2 text-center">
                      Maximum container width
                    </p>
                  </div>
                </div>

                {/* Min Height */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Min Height
                  </label>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                    {/* Height Presets */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {[
                        { value: 'auto', label: 'Auto' },
                        { value: '200px', label: '200px' },
                        { value: '300px', label: '300px' },
                        { value: '400px', label: '400px' },
                        { value: '50vh', label: '50vh' },
                        { value: '100vh', label: '100vh' }
                      ].map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('minHeight', preset.value)}
                          className={`p-2 text-xs font-medium rounded border-2 transition-all ${(content?.minHeight || '') === preset.value
                            ? 'border-purple-500 bg-purple-100 text-purple-700'
                            : 'border-purple-200 hover:border-purple-300 bg-white text-purple-700'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={content?.minHeight || ''}
                      onChange={(e) => updateGallerySetting('minHeight', e.target.value)}
                      className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                      placeholder="e.g., 300px, 20rem"
                    />
                    <p className="text-xs text-purple-600 mt-2 text-center">
                      Minimum container height
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Container Preview */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-6 border-2 border-dashed border-rose-300">
              <div className="text-center mb-6">
                <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm border border-rose-200">
                  <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></div>
                  <span className="text-gray-700 font-medium"> Container Preview</span>
                </div>
              </div>

              {/* Live Container Preview */}
              <div className="flex justify-center mb-6">
                <div
                  className="relative bg-gray-100 p-4 rounded-lg"
                  style={{
                    maxWidth: content?.maxWidth || '300px',
                    minHeight: content?.minHeight || 'auto'
                  }}
                >
                  <div
                    className="w-full h-32 flex items-center justify-center text-gray-600 font-medium relative"
                    style={{
                      backgroundColor: content?.backgroundColor || '#ffffff',
                      borderColor: content?.borderColor || '#e5e7eb',
                      borderWidth: `${content?.borderWidth || '0'}px`,
                      borderRadius: content?.borderRadius === 'none' ? '0' :
                        content?.borderRadius === 'rounded-sm' ? '2px' :
                          content?.borderRadius === 'rounded' ? '4px' :
                            content?.borderRadius === 'rounded-lg' ? '8px' :
                              content?.borderRadius === 'rounded-xl' ? '12px' :
                                content?.borderRadius === 'rounded-full' ? '50px' : '4px',
                      boxShadow: content?.boxShadow === 'none' ? 'none' :
                        content?.boxShadow === 'small' ? '0 1px 2px 0 rgb(0 0 0 / 0.05)' :
                          content?.boxShadow === 'medium' ? '0 4px 6px -1px rgb(0 0 0 / 0.1)' :
                            content?.boxShadow === 'large' ? '0 10px 15px -3px rgb(0 0 0 / 0.1)' :
                              content?.boxShadow === 'xl' ? '0 20px 25px -5px rgb(0 0 0 / 0.1)' : 'none',
                      padding: content?.containerPadding || '16px'
                    }}
                  >
                    Gallery Container

                    {/* Visual Indicators */}
                    {content?.containerPadding && (
                      <div className="absolute inset-0 border-2 border-dashed border-emerald-400 opacity-30 pointer-events-none"></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Style Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-lg shadow-sm border border-rose-200 text-center">
                  <div className="text-xs text-gray-600">Background</div>
                  <div
                    className="w-6 h-6 rounded border mx-auto mt-1"
                    style={{ backgroundColor: content?.backgroundColor || '#ffffff' }}
                  ></div>
                  <div className="text-xs font-medium text-gray-700 mt-1">
                    {content?.backgroundColor || '#ffffff'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-rose-200 text-center">
                  <div className="text-xs text-gray-600">Border</div>
                  <div className="text-sm font-medium text-rose-600">
                    {content?.borderWidth || '0'}px
                  </div>
                  <div className="text-xs text-gray-500">
                    {content?.borderRadius || 'rounded'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-rose-200 text-center">
                  <div className="text-xs text-gray-600">Shadow</div>
                  <div className="text-sm font-medium text-rose-600">
                    {content?.boxShadow || 'none'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-rose-200 text-center">
                  <div className="text-xs text-gray-600">Padding</div>
                  <div className="text-sm font-medium text-rose-600">
                    {content?.containerPadding || 'none'}
                  </div>
                </div>
              </div>

              <div className="text-center mt-4">
                <p className="text-xs text-rose-600">
                  Live preview updates as you modify container styles
                </p>
              </div>
            </div>
          </div>


          {/* Enhanced Modal Settings */}
          <div className="mt-6 bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white rounded-sm relative">
                  <div className="absolute inset-1 bg-white rounded-sm"></div>
                </div>
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900">Modal Settings</h4>
                <p className="text-sm text-slate-600">Configure modal behavior, appearance, and user experience</p>
              </div>
            </div>

            {/* Color Configuration */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-violet-500 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 rounded-full bg-white"></div>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Color Scheme</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Modal Background Color */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Modal Background (Overlay)
                  </label>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                    {/* Color Presets */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {[
                        { color: '#000000', label: 'Black', opacity: '0.8' },
                        { color: '#1f2937', label: 'Dark Gray', opacity: '0.9' },
                        { color: '#374151', label: 'Gray', opacity: '0.7' },
                        { color: '#ffffff', label: 'White', opacity: '0.9' }
                      ].map(preset => (
                        <button
                          key={preset.color}
                          onClick={() => updateGallerySetting('modalBackgroundColor', preset.color)}
                          className={`w-full h-12 rounded-lg border-2 transition-all shadow-sm hover:shadow-md relative overflow-hidden ${(content?.modalBackgroundColor || '#000000') === preset.color
                            ? 'border-purple-500 ring-2 ring-purple-200 scale-105'
                            : 'border-gray-300 hover:border-purple-300'
                            }`}
                          style={{ backgroundColor: preset.color }}
                          title={preset.label}
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className={`text-xs font-medium ${preset.color === '#ffffff' ? 'text-gray-700' : 'text-white'}`}>
                              {preset.label}
                            </span>
                          </div>
                          {(content?.modalBackgroundColor || '#000000') === preset.color && (
                            <div className="absolute top-1 right-1">
                              <div className="w-3 h-3 bg-purple-500 rounded-full flex items-center justify-center">
                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                              </div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Custom Color Picker */}
                    <input
                      type="color"
                      className="w-full h-12 border-2 border-purple-200 rounded-lg cursor-pointer"
                      value={content?.modalBackgroundColor || '#000000'}
                      onChange={(e) => updateGallerySetting('modalBackgroundColor', e.target.value)}
                    />
                    <p className="text-xs text-purple-600 mt-2 text-center font-medium">
                      Current: {content?.modalBackgroundColor || '#000000'}
                    </p>
                  </div>
                </div>

                {/* Modal Content Background */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Modal Content Background
                  </label>
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-lg border border-violet-200">
                    {/* Content Background Presets */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {[
                        { color: '#ffffff', label: 'White' },
                        { color: '#f8fafc', label: 'Light' },
                        { color: '#f1f5f9', label: 'Slate' },
                        { color: '#1f2937', label: 'Dark' }
                      ].map(preset => (
                        <button
                          key={preset.color}
                          onClick={() => updateGallerySetting('modalContentBackground', preset.color)}
                          className={`w-full h-12 rounded-lg border-2 transition-all shadow-sm hover:shadow-md relative overflow-hidden ${(content?.modalContentBackground || '#ffffff') === preset.color
                            ? 'border-violet-500 ring-2 ring-violet-200 scale-105'
                            : 'border-gray-300 hover:border-violet-300'
                            }`}
                          style={{ backgroundColor: preset.color }}
                          title={preset.label}
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className={`text-xs font-medium ${preset.color === '#ffffff' || preset.color === '#f8fafc' || preset.color === '#f1f5f9' ? 'text-gray-700' : 'text-white'}`}>
                              {preset.label}
                            </span>
                          </div>
                          {(content?.modalContentBackground || '#ffffff') === preset.color && (
                            <div className="absolute top-1 right-1">
                              <div className="w-3 h-3 bg-violet-500 rounded-full flex items-center justify-center">
                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                              </div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Custom Content Color Picker */}
                    <input
                      type="color"
                      className="w-full h-12 border-2 border-violet-200 rounded-lg cursor-pointer"
                      value={content?.modalContentBackground || '#ffffff'}
                      onChange={(e) => updateGallerySetting('modalContentBackground', e.target.value)}
                    />
                    <p className="text-xs text-violet-600 mt-2 text-center font-medium">
                      Current: {content?.modalContentBackground || '#ffffff'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Dimensions Configuration */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white rounded"></div>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Modal Dimensions</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Max Width Settings */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Maximum Width
                  </label>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                    {/* Width Presets */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {[
                        { value: '80vw', label: '80% Viewport' },
                        { value: '90vw', label: '90% Viewport' },
                        { value: '600px', label: '600px' },
                        { value: '800px', label: '800px' },
                        { value: '1200px', label: '1200px' },
                        { value: '100vw', label: 'Full Width' }
                      ].map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('modalMaxWidth', preset.value)}
                          className={`p-3 text-xs font-medium rounded-lg border-2 transition-all text-center ${(content?.modalMaxWidth || '90vw') === preset.value
                            ? 'border-blue-500 bg-blue-100 text-blue-700 shadow-md'
                            : 'border-blue-200 hover:border-blue-300 bg-white text-blue-700'
                            }`}
                        >
                          <div className="font-bold text-sm">{preset.label}</div>
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={content?.modalMaxWidth || '90vw'}
                      onChange={(e) => updateGallerySetting('modalMaxWidth', e.target.value)}
                      className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                      placeholder="e.g., 90vw, 800px"
                    />
                    <p className="text-xs text-blue-600 mt-2 text-center">
                      Controls modal width on different screen sizes
                    </p>
                  </div>
                </div>

                {/* Max Height Settings */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Maximum Height
                  </label>
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-lg border border-cyan-200">
                    {/* Height Presets */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {[
                        { value: '80vh', label: '80% Viewport' },
                        { value: '90vh', label: '90% Viewport' },
                        { value: '600px', label: '600px' },
                        { value: '800px', label: '800px' },
                        { value: '100vh', label: 'Full Height' },
                        { value: 'auto', label: 'Auto Height' }
                      ].map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('modalMaxHeight', preset.value)}
                          className={`p-3 text-xs font-medium rounded-lg border-2 transition-all text-center ${(content?.modalMaxHeight || '90vh') === preset.value
                            ? 'border-cyan-500 bg-cyan-100 text-cyan-700 shadow-md'
                            : 'border-cyan-200 hover:border-cyan-300 bg-white text-cyan-700'
                            }`}
                        >
                          <div className="font-bold text-sm">{preset.label}</div>
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={content?.modalMaxHeight || '90vh'}
                      onChange={(e) => updateGallerySetting('modalMaxHeight', e.target.value)}
                      className="w-full p-3 border-2 border-cyan-200 rounded-lg focus:border-cyan-500 focus:ring-0 transition-colors"
                      placeholder="e.g., 90vh, 600px"
                    />
                    <p className="text-xs text-cyan-600 mt-2 text-center">
                      Controls modal height to prevent overflow
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Effects */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full opacity-70"></div>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Visual Effects</h5>
              </div>

              <div className="space-y-4">
                {/* Background Blur Toggle */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-5 rounded-xl border border-emerald-200 shadow-sm">
                  <label className="flex items-start gap-4 cursor-pointer">
                    <div className="relative mt-1">
                      <input
                        type="checkbox"
                        checked={content?.modalBlur || false}
                        onChange={(e) => updateGallerySetting('modalBlur', e.target.checked)}
                        className="w-6 h-6 text-emerald-600 border-2 border-gray-300 rounded-lg focus:ring-emerald-500 focus:ring-2 transition-all"
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        {content?.modalBlur && (
                          <div className="w-4 h-4 text-emerald-600">
                            <div className="w-full h-full bg-emerald-600 rounded-sm"></div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div>
                          <span className="text-lg font-semibold text-emerald-800">Enable Background Blur</span>
                          {content?.modalBlur && (
                            <div className="inline-block ml-2 w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-emerald-700">
                        Applies a beautiful blur effect to the background content when modal is open
                      </p>
                      <div className="mt-3 text-xs text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full inline-block">
                        {content?.modalBlur ? 'Blur Effect Active' : 'No Blur Effect'}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Preview */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border-2 border-dashed border-slate-300">
              <div className="text-center mb-6">
                <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm border border-slate-200">
                  <div className="w-2 h-2 bg-slate-500 rounded-full animate-pulse"></div>
                  <span className="text-gray-700 font-medium">Modal Preview</span>
                </div>
              </div>

              {/* Mock Modal Preview */}
              <div className="relative mx-auto max-w-md">
                {/* Background simulation */}
                <div
                  className={`absolute inset-0 rounded-lg ${content?.modalBlur ? 'blur-sm' : ''}`}
                  style={{ backgroundColor: content?.modalBackgroundColor || '#000000', opacity: 0.7 }}
                ></div>

                {/* Modal content simulation */}
                <div
                  className="relative z-10 p-6 rounded-lg shadow-xl border-2 border-gray-200"
                  style={{
                    backgroundColor: content?.modalContentBackground || '#ffffff',
                    maxWidth: '300px',
                    margin: '0 auto'
                  }}
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg mx-auto mb-4 flex items-center justify-center">
                      <div className="w-8 h-8 bg-gray-400 rounded"></div>
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">Modal Content</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      This is how your modal will appear when users click on gallery items.
                    </p>
                    <div className="w-6 h-6 bg-gray-300 rounded-full mx-auto"></div>
                  </div>
                </div>
              </div>

              {/* Settings Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Background</div>
                  <div
                    className="w-6 h-6 rounded border mx-auto"
                    style={{ backgroundColor: content?.modalBackgroundColor || '#000000' }}
                  ></div>
                  <div className="text-xs font-medium text-gray-700 mt-1">
                    {content?.modalBackgroundColor || '#000000'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Content</div>
                  <div
                    className="w-6 h-6 rounded border mx-auto"
                    style={{ backgroundColor: content?.modalContentBackground || '#ffffff' }}
                  ></div>
                  <div className="text-xs font-medium text-gray-700 mt-1">
                    {content?.modalContentBackground || '#ffffff'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Width</div>
                  <div className="text-sm font-medium text-slate-600">
                    {content?.modalMaxWidth || '90vw'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Height</div>
                  <div className="text-sm font-medium text-slate-600">
                    {content?.modalMaxHeight || '90vh'}
                  </div>
                </div>
              </div>


            </div>
          </div>

          {/* Enhanced Caption Settings */}
          <div className="mt-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <div className="w-6 h-4 bg-white rounded-sm relative">
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-amber-500 rounded-b-sm"></div>
                </div>
              </div>
              <div>
                <h4 className="text-xl font-bold text-amber-900">Caption Settings</h4>
                <p className="text-sm text-amber-700">Configure caption appearance, positioning, and styling</p>
              </div>
            </div>

            {/* Position & Style Configuration */}
            <div className="bg-white rounded-xl p-6 border border-amber-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-3 bg-white rounded-sm"></div>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Position & Style</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Caption Position */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Caption Position
                  </label>
                  <div className="space-y-2">
                    {[
                      {
                        value: 'none',
                        label: 'Hidden',
                        description: 'No captions shown',
                        preview: 'No caption display'
                      },
                      {
                        value: 'bottom',
                        label: 'Bottom',
                        description: 'Below the image',
                        preview: 'Caption appears under image'
                      },
                      {
                        value: 'top',
                        label: 'Top',
                        description: 'Above the image',
                        preview: 'Caption appears above image'
                      },
                      {
                        value: 'overlay',
                        label: 'Overlay',
                        description: 'Over the image',
                        preview: 'Caption overlays on image'
                      }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => updateGallerySetting('captionPosition', option.value)}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left hover:shadow-md ${(content?.captionPosition || 'bottom') === option.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-lg ring-2 ring-indigo-200'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h6 className="font-semibold">{option.label}</h6>
                              {(content?.captionPosition || 'bottom') === option.value && (
                                <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{option.description}</p>
                            <p className="text-xs text-gray-500 mt-1">{option.preview}</p>
                          </div>

                          {/* Visual Position Indicator */}
                          <div className="w-12 h-10 bg-gray-100 rounded border relative ml-3">
                            <div className="w-full h-6 bg-gray-300 rounded-sm"></div>
                            {option.value === 'top' && (
                              <div className="absolute -top-1 left-0 right-0 h-1 bg-indigo-400 rounded"></div>
                            )}
                            {option.value === 'bottom' && (
                              <div className="absolute -bottom-1 left-0 right-0 h-1 bg-indigo-400 rounded"></div>
                            )}
                            {option.value === 'overlay' && (
                              <div className="absolute bottom-1 left-1 right-1 h-1 bg-indigo-400 rounded"></div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Caption Style */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Caption Text Style
                  </label>
                  <div className="space-y-2">
                    {[
                      {
                        value: 'normal',
                        label: 'Normal',
                        description: 'Regular text style',
                        style: { fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'none' }
                      },
                      {
                        value: 'bold',
                        label: 'Bold',
                        description: 'Bold text weight',
                        style: { fontWeight: 'bold', fontStyle: 'normal', textDecoration: 'none' }
                      },
                      {
                        value: 'italic',
                        label: 'Italic',
                        description: 'Italicized text',
                        style: { fontWeight: 'normal', fontStyle: 'italic', textDecoration: 'none' }
                      },
                      {
                        value: 'underline',
                        label: 'Underline',
                        description: 'Underlined text',
                        style: { fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'underline' }
                      }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => updateGallerySetting('captionStyle', option.value)}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left hover:shadow-md ${(content?.captionStyle || 'normal') === option.value
                          ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-lg ring-2 ring-purple-200'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h6 className="font-semibold">{option.label}</h6>
                              {(content?.captionStyle || 'normal') === option.value && (
                                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{option.description}</p>
                          </div>

                          {/* Style Preview */}
                          <div className="ml-3">
                            <span
                              className="text-sm text-gray-700 px-2 py-1 bg-gray-100 rounded"
                              style={option.style}
                            >
                              Sample
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Typography & Color Configuration */}
            <div className="bg-white rounded-xl p-6 border border-amber-200 shadow-sm mb-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 text-white font-bold text-xs flex items-center justify-center">
                    Aa
                  </div>
                </div>
                <h5 className="text-lg font-semibold text-gray-800">Typography & Colors</h5>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Font Size */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Font Size
                  </label>
                  <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200">
                    {/* Size Presets */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {[
                        { value: '12px', label: 'Small' },
                        { value: '14px', label: 'Medium' },
                        { value: '16px', label: 'Large' },
                        { value: '18px', label: 'X-Large' }
                      ].map(preset => (
                        <button
                          key={preset.value}
                          onClick={() => updateGallerySetting('captionFontSize', preset.value)}
                          className={`p-2 text-xs font-medium rounded border-2 transition-all ${(content?.captionFontSize || '14px') === preset.value
                            ? 'border-rose-500 bg-rose-100 text-rose-700'
                            : 'border-rose-200 hover:border-rose-300 bg-white text-rose-700'
                            }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={content?.captionFontSize || '14px'}
                      onChange={(e) => updateGallerySetting('captionFontSize', e.target.value)}
                      className="w-full p-3 border-2 border-rose-200 rounded-lg focus:border-rose-500 focus:ring-0 transition-colors"
                      placeholder="e.g., 14px, 0.875rem"
                    />
                    <p className="text-xs text-rose-600 mt-2 text-center">
                      Current: {content?.captionFontSize || '14px'}
                    </p>
                  </div>
                </div>

                {/* Text Color */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Text Color
                  </label>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                    {/* Color Presets */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[
                        { color: '#000000', label: 'Black' },
                        { color: '#666666', label: 'Gray' },
                        { color: '#ffffff', label: 'White' },
                        { color: '#1f2937', label: 'Dark' }
                      ].map(preset => (
                        <button
                          key={preset.color}
                          onClick={() => updateGallerySetting('captionColor', preset.color)}
                          className={`w-8 h-8 rounded border-2 transition-all hover:scale-110 ${(content?.captionColor || '#666666') === preset.color
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-300 hover:border-blue-300'
                            }`}
                          style={{ backgroundColor: preset.color }}
                          title={preset.label}
                        >
                          {(content?.captionColor || '#666666') === preset.color && (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    <input
                      type="color"
                      className="w-full h-10 border-2 border-blue-200 rounded-lg cursor-pointer"
                      value={content?.captionColor || '#666666'}
                      onChange={(e) => updateGallerySetting('captionColor', e.target.value)}
                    />
                    <p className="text-xs text-blue-600 mt-2 text-center font-medium">
                      {content?.captionColor || '#666666'}
                    </p>
                  </div>
                </div>

                {/* Background Color (for overlay) */}
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    Background Color (Overlay)
                  </label>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                    {/* Background Color Presets */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[
                        { color: '#000000', label: 'Black' },
                        { color: '#1f2937', label: 'Dark' },
                        { color: '#ffffff', label: 'White' },
                        { color: 'transparent', label: 'Clear' }
                      ].map(preset => (
                        <button
                          key={preset.color}
                          onClick={() => updateGallerySetting('captionBackgroundColor', preset.color)}
                          className={`w-8 h-8 rounded border-2 transition-all hover:scale-110 relative ${(content?.captionBackgroundColor || '#000000') === preset.color
                            ? 'border-purple-500 ring-2 ring-purple-200'
                            : 'border-gray-300 hover:border-purple-300'
                            }`}
                          style={{
                            backgroundColor: preset.color === 'transparent' ? '#ffffff' : preset.color,
                            backgroundImage: preset.color === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 'none',
                            backgroundSize: preset.color === 'transparent' ? '4px 4px' : 'auto',
                            backgroundPosition: preset.color === 'transparent' ? '0 0, 0 2px, 2px -2px, -2px 0px' : 'auto'
                          }}
                          title={preset.label}
                        >
                          {(content?.captionBackgroundColor || '#000000') === preset.color && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    <input
                      type="color"
                      className="w-full h-10 border-2 border-purple-200 rounded-lg cursor-pointer"
                      value={content?.captionBackgroundColor || '#000000'}
                      onChange={(e) => updateGallerySetting('captionBackgroundColor', e.target.value)}
                    />
                    <p className="text-xs text-purple-600 mt-2 text-center font-medium">
                      {content?.captionBackgroundColor || '#000000'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Caption Preview */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-dashed border-amber-300">
              <div className="text-center mb-6">
                <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm border border-amber-200">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                  <span className="text-gray-700 font-medium">Caption Preview</span>
                </div>
              </div>

              {/* Live Caption Preview */}
              <div className="flex justify-center mb-6">
                <div className="relative bg-white rounded-lg shadow-sm border border-amber-200 overflow-hidden" style={{ width: '280px' }}>
                  {/* Top caption */}
                  {content?.captionPosition === 'top' && (
                    <div
                      className="p-3 text-center"
                      style={{
                        fontSize: content?.captionFontSize || '14px',
                        color: content?.captionColor || '#666666',
                        fontWeight: content?.captionStyle === 'bold' ? 'bold' : 'normal',
                        fontStyle: content?.captionStyle === 'italic' ? 'italic' : 'normal',
                        textDecoration: content?.captionStyle === 'underline' ? 'underline' : 'none'
                      }}
                    >
                      Sample Caption Text
                    </div>
                  )}

                  {/* Image placeholder */}
                  <div className="relative bg-gradient-to-br from-gray-200 to-gray-300 h-40 flex items-center justify-center">
                    <div className="text-gray-500 text-sm">Gallery Image</div>

                    {/* Overlay caption */}
                    {content?.captionPosition === 'overlay' && (
                      <div
                        className="absolute bottom-0 left-0 right-0 p-3 text-center"
                        style={{
                          backgroundColor: content?.captionBackgroundColor || '#000000',
                          opacity: 0.9
                        }}
                      >
                        <div
                          style={{
                            fontSize: content?.captionFontSize || '14px',
                            color: content?.captionColor || '#666666',
                            fontWeight: content?.captionStyle === 'bold' ? 'bold' : 'normal',
                            fontStyle: content?.captionStyle === 'italic' ? 'italic' : 'normal',
                            textDecoration: content?.captionStyle === 'underline' ? 'underline' : 'none'
                          }}
                        >
                          Sample Caption Text
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bottom caption */}
                  {content?.captionPosition === 'bottom' && (
                    <div
                      className="p-3 text-center"
                      style={{
                        fontSize: content?.captionFontSize || '14px',
                        color: content?.captionColor || '#666666',
                        fontWeight: content?.captionStyle === 'bold' ? 'bold' : 'normal',
                        fontStyle: content?.captionStyle === 'italic' ? 'italic' : 'normal',
                        textDecoration: content?.captionStyle === 'underline' ? 'underline' : 'none'
                      }}
                    >
                      Sample Caption Text
                    </div>
                  )}

                  {/* Hidden state */}
                  {content?.captionPosition === 'none' && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="text-white text-sm font-medium">No Caption Shown</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Settings Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-lg shadow-sm border border-amber-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Position</div>
                  <div className="text-sm font-medium text-amber-600">
                    {content?.captionPosition || 'bottom'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-amber-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Style</div>
                  <div className="text-sm font-medium text-amber-600">
                    {content?.captionStyle || 'normal'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-amber-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Size</div>
                  <div className="text-sm font-medium text-amber-600">
                    {content?.captionFontSize || '14px'}
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg shadow-sm border border-amber-200 text-center">
                  <div className="text-xs text-gray-600 mb-1">Color</div>
                  <div
                    className="w-6 h-6 rounded border mx-auto"
                    style={{ backgroundColor: content?.captionColor || '#666666' }}
                  ></div>
                </div>
              </div>

            </div>
          </div>




          {/* Enhanced Gallery Items */}
          <div className="mt-6 bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <div className="grid grid-cols-2 gap-0.5 w-5 h-5">
                    <div className="bg-white rounded-sm"></div>
                    <div className="bg-white rounded-sm"></div>
                    <div className="bg-white rounded-sm"></div>
                    <div className="bg-white rounded-sm"></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-teal-900">Gallery Items</h4>
                  <p className="text-sm text-teal-700">Manage your gallery media items and their settings</p>
                </div>
              </div>

              {/* Add Item Button */}
              <button
                type="button"
                className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:from-teal-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
                onClick={addImage}
              >
                <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                Add Media Item
              </button>
            </div>

            {/* Items List */}
            <div className="space-y-6">
              {(content?.images || []).length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-teal-300">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="grid grid-cols-2 gap-1 w-8 h-8">
                      <div className="bg-teal-400 rounded-sm"></div>
                      <div className="bg-teal-400 rounded-sm"></div>
                      <div className="bg-teal-400 rounded-sm"></div>
                      <div className="bg-teal-400 rounded-sm"></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Items Yet</h3>
                  <p className="text-gray-600 mb-4">Start building your gallery by adding images or videos</p>
                  <button
                    type="button"
                    className="bg-teal-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors"
                    onClick={addImage}
                  >
                    Add Your First Item
                  </button>
                </div>
              ) : (
                (content?.images || []).map((image, index) => (
                  <div key={index} className="bg-white rounded-xl border border-teal-200 shadow-sm overflow-hidden">
                    {/* Item Header */}
                    <div className="bg-gradient-to-r from-teal-500 to-emerald-600 p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">{index + 1}</span>
                          </div>
                          <div>
                            <h5 className="text-white font-semibold text-lg">
                              Item {index + 1}
                            </h5>
                            <p className="text-teal-100 text-sm">
                              {image.type === 'video' ? 'Video Content' : 'Image Content'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                          onClick={() => removeImage(index)}
                        >
                          <div className="w-4 h-4">
                            <div className="w-full h-0.5 bg-white transform rotate-45 translate-y-1.5"></div>
                            <div className="w-full h-0.5 bg-white transform -rotate-45 -translate-y-1.5"></div>
                          </div>
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Item Content */}
                    <div className="p-6">
                      {/* Media Type & Upload Section */}
                      <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center">
                            <div className="w-4 h-4 bg-white rounded-sm"></div>
                          </div>
                          <h6 className="text-lg font-semibold text-gray-800">Media Configuration</h6>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Media Type Selection */}
                          <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-700">Media Type</label>
                            <div className="space-y-2">
                              {[
                                { value: 'image', label: 'Image', desc: 'Photos, graphics, illustrations' },
                                { value: 'video', label: 'Video', desc: 'MP4, WebM, and other video formats' }
                              ].map(option => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => updateImage(index, 'type', option.value)}
                                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${(image.type || 'image') === option.value
                                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md ring-2 ring-blue-200'
                                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                    }`}
                                >
                                  <div className="flex items-center gap-3">

                                    <div>
                                      <div className="font-medium">{option.label}</div>
                                      <div className="text-sm text-gray-500">{option.desc}</div>
                                    </div>
                                    {(image.type || 'image') === option.value && (
                                      <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Media Upload */}
                          <div className="space-y-4">
                            <label className="text-sm font-medium text-gray-700">
                              {image.type === 'video' ? 'Video Upload' : 'Image Upload'}
                            </label>
                            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-4">
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors mb-3"
                                placeholder={image.type === 'video' ? "Video URL or upload below" : "Image URL or upload below"}
                                value={image.url || ''}
                                onChange={(e) => updateImage(index, 'url', e.target.value)}
                              />

                              {image.type === 'video' ? (
                                <VideoUploader
                                  onVideoUpload={(path) => updateImage(index, 'url', path)}
                                  pageSlug={pageSlug}
                                />
                              ) : (
                                <ImageUploader
                                  onImageUpload={(path) => updateImage(index, 'url', path)}
                                  pageSlug={pageSlug}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Container Dimensions & Styling */}
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-6">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-violet-500 rounded-lg flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-white rounded"></div>
                          </div>
                          <h6 className="text-lg font-semibold text-gray-800">Container Dimensions & Styling</h6>
                        </div>

                        <div className="space-y-6">
                          {/* Dimensions */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Container Width</label>
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                placeholder="e.g., 300px, 50%, auto"
                                value={image.containerWidth || ''}
                                onChange={(e) => updateImage(index, 'containerWidth', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Container Height</label>
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                placeholder="e.g., 200px, auto, 100vh"
                                value={image.containerHeight || ''}
                                onChange={(e) => updateImage(index, 'containerHeight', e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Spacing */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Container Padding</label>
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                placeholder="e.g., 1rem, 16px"
                                value={image.containerPadding || ''}
                                onChange={(e) => updateImage(index, 'containerPadding', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Container Margin</label>
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                placeholder="e.g., 0.5rem, auto"
                                value={image.containerMargin || ''}
                                onChange={(e) => updateImage(index, 'containerMargin', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Border Radius</label>
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                placeholder="e.g., 8px, 50%"
                                value={image.containerBorderRadius || ''}
                                onChange={(e) => updateImage(index, 'containerBorderRadius', e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Styling */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                              <input
                                type="color"
                                className="w-full h-12 border-2 border-purple-200 rounded-lg cursor-pointer"
                                value={image.containerBackground || '#ffffff'}
                                onChange={(e) => updateImage(index, 'containerBackground', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Container Shadow</label>
                              <select
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                value={image.containerShadow || 'none'}
                                onChange={(e) => updateImage(index, 'containerShadow', e.target.value)}
                              >
                                <option value="none">None</option>
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                                <option value="custom">Custom</option>
                              </select>
                            </div>
                          </div>

                          {/* Custom Shadow */}
                          {image.containerShadow === 'custom' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Custom Shadow</label>
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                placeholder="e.g., 0 4px 8px rgba(0,0,0,0.1)"
                                value={image.customContainerShadow || ''}
                                onChange={(e) => updateImage(index, 'customContainerShadow', e.target.value)}
                              />
                            </div>
                          )}

                          {/* Advanced Properties */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                              <select
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                value={image.containerPosition || 'relative'}
                                onChange={(e) => updateImage(index, 'containerPosition', e.target.value)}
                              >
                                <option value="static">Static</option>
                                <option value="relative">Relative</option>
                                <option value="absolute">Absolute</option>
                                <option value="sticky">Sticky</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Z-Index</label>
                              <input
                                type="number"
                                className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-0 transition-colors"
                                value={image.containerZIndex || ''}
                                onChange={(e) => updateImage(index, 'containerZIndex', parseInt(e.target.value))}
                                placeholder="0"
                              />
                            </div>
                          </div>

                          {/* Container Options */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-center gap-3 p-4 bg-white rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={image.containerOverflowHidden || false}
                                onChange={(e) => updateImage(index, 'containerOverflowHidden', e.target.checked)}
                                className="w-5 h-5 text-purple-600 border-2 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-800">Hide Container Overflow</span>
                                <p className="text-xs text-gray-600">Clips content outside container bounds</p>
                              </div>
                            </label>
                            <label className="flex items-center gap-3 p-4 bg-white rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={image.containerIsolate || false}
                                onChange={(e) => updateImage(index, 'containerIsolate', e.target.checked)}
                                className="w-5 h-5 text-purple-600 border-2 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-800">Isolate Container Context</span>
                                <p className="text-xs text-gray-600">Creates new stacking context</p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Media Element Dimensions */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-6">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center">
                            <div className="w-4 h-3 bg-white rounded-sm"></div>
                          </div>
                          <h6 className="text-lg font-semibold text-gray-800">Media Element Dimensions</h6>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Media Width</label>
                            <input
                              type="text"
                              className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                              placeholder="e.g., 100%, 200px, auto"
                              value={image.width || ''}
                              onChange={(e) => updateImage(index, 'width', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Media Height</label>
                            <input
                              type="text"
                              className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                              placeholder="e.g., auto, 150px"
                              value={image.height || ''}
                              onChange={(e) => updateImage(index, 'height', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Object Fit</label>
                            <select
                              className="w-full p-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors"
                              value={image.objectFit || 'cover'}
                              onChange={(e) => updateImage(index, 'objectFit', e.target.value)}
                            >
                              <option value="cover">Cover</option>
                              <option value="contain">Contain</option>
                              <option value="fill">Fill</option>
                              <option value="scale-down">Scale Down</option>
                              <option value="none">None</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Video Access Control */}
                      {image.type === 'video' && (
                        <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 mb-6">
                          <div className="flex items-center gap-2 mb-6">
                            <div className="w-8 h-8 bg-gradient-to-br from-red-400 to-pink-500 rounded-lg flex items-center justify-center">
                              <div className="w-4 h-4 bg-white rounded-full"></div>
                            </div>
                            <h6 className="text-lg font-semibold text-gray-800">Video Access Control</h6>
                          </div>

                          <div className="space-y-6">
                            {/* Access Level */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Video Access</label>
                                <select
                                  className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                  value={image.videoAccess || 'public'}
                                  onChange={(e) => updateImage(index, 'videoAccess', e.target.value)}
                                >
                                  <option value="public">Public</option>
                                  <option value="locked">Locked</option>
                                </select>
                              </div>

                              {image.videoAccess === 'locked' && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Unlock Condition</label>
                                  <select
                                    className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                    value={image.unlockCondition || 'login'}
                                    onChange={(e) => updateImage(index, 'unlockCondition', e.target.value)}
                                  >
                                    <option value="login">User Login Required</option>
                                    <option value="subscription">Active Subscription</option>
                                    <option value="payment">One-time Payment</option>
                                    <option value="form">Form Completion</option>
                                    <option value="password">Password Protected</option>
                                    <option value="time">Time-based Access</option>
                                  </select>
                                </div>
                              )}
                            </div>

                            {/* Condition-specific settings */}
                            {image.videoAccess === 'locked' && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {image.unlockCondition === 'payment' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Price ($)</label>
                                    <input
                                      type="number"
                                      className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                      value={image.unlockPrice || ''}
                                      onChange={(e) => updateImage(index, 'unlockPrice', e.target.value)}
                                      placeholder="9.99"
                                      step="0.01"
                                    />
                                  </div>
                                )}

                                {image.unlockCondition === 'password' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Access Password</label>
                                    <input
                                      type="password"
                                      className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                      value={image.unlockPassword || ''}
                                      onChange={(e) => updateImage(index, 'unlockPassword', e.target.value)}
                                      placeholder="Enter password"
                                    />
                                  </div>
                                )}

                                {image.unlockCondition === 'subscription' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Required Subscription Level</label>
                                    <select
                                      className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                      value={image.subscriptionLevel || 'basic'}
                                      onChange={(e) => updateImage(index, 'subscriptionLevel', e.target.value)}
                                    >
                                      <option value="basic">Basic</option>
                                      <option value="premium">Premium</option>
                                      <option value="pro">Pro</option>
                                    </select>
                                  </div>
                                )}

                                {image.unlockCondition === 'time' && (
                                  <>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Available From</label>
                                      <input
                                        type="datetime-local"
                                        className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                        value={image.availableFrom || ''}
                                        onChange={(e) => updateImage(index, 'availableFrom', e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Available Until</label>
                                      <input
                                        type="datetime-local"
                                        className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                        value={image.availableUntil || ''}
                                        onChange={(e) => updateImage(index, 'availableUntil', e.target.value)}
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Lock overlay customization */}
                            {image.videoAccess === 'locked' && (
                              <div className="bg-white rounded-lg p-4 border border-red-200">
                                <h6 className="font-medium mb-4 text-gray-800">Lock Overlay Customization</h6>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Lock Icon</label>
                                    <select
                                      className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                      value={image.lockIcon || 'lock'}
                                      onChange={(e) => updateImage(index, 'lockIcon', e.target.value)}
                                    >
                                      <option value="lock">🔒 Lock</option>
                                      <option value="key">🔑 Key</option>
                                      <option value="crown">👑 Crown</option>
                                      <option value="diamond">💎 Diamond</option>
                                      <option value="star">⭐ Star</option>
                                      <option value="custom">Custom Icon</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Lock Message</label>
                                    <input
                                      type="text"
                                      className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                      value={image.lockMessage || 'This video is locked'}
                                      onChange={(e) => updateImage(index, 'lockMessage', e.target.value)}
                                      placeholder="Custom lock message"
                                    />
                                  </div>
                                </div>

                                {image.lockIcon === 'custom' && (
                                  <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Custom Icon URL</label>
                                    <input
                                      type="text"
                                      className="w-full p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:ring-0 transition-colors"
                                      value={image.customLockIcon || ''}
                                      onChange={(e) => updateImage(index, 'customLockIcon', e.target.value)}
                                      placeholder="https://example.com/icon.png"
                                    />
                                  </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Color</label>
                                    <input
                                      type="color"
                                      className="w-full h-12 border-2 border-red-200 rounded-lg cursor-pointer"
                                      value={image.lockOverlayColor || '#000000'}
                                      onChange={(e) => updateImage(index, 'lockOverlayColor', e.target.value)}
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Opacity</label>
                                    <input
                                      type="range"
                                      className="w-full mt-2"
                                      min="0"
                                      max="1"
                                      step="0.1"
                                      value={image.lockOverlayOpacity || '0.8'}
                                      onChange={(e) => updateImage(index, 'lockOverlayOpacity', e.target.value)}
                                    />
                                    <span className="text-sm text-gray-500 block text-center mt-1">
                                      {Math.round((image.lockOverlayOpacity || 0.8) * 100)}%
                                    </span>
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                                    <input
                                      type="color"
                                      className="w-full h-12 border-2 border-red-200 rounded-lg cursor-pointer"
                                      value={image.lockTextColor || '#ffffff'}
                                      onChange={(e) => updateImage(index, 'lockTextColor', e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Basic Information */}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-6">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center">
                            <div className="w-4 h-3 bg-white rounded-sm"></div>
                          </div>
                          <h6 className="text-lg font-semibold text-gray-800">Basic Information</h6>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
                            <input
                              type="text"
                              className="w-full p-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-0 transition-colors"
                              value={image.alt || ''}
                              onChange={(e) => updateImage(index, 'alt', e.target.value)}
                              placeholder="Descriptive text for accessibility"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Caption</label>
                            <input
                              type="text"
                              className="w-full p-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-0 transition-colors"
                              value={image.caption || ''}
                              onChange={(e) => updateImage(index, 'caption', e.target.value)}
                              placeholder="Caption text to display"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Interaction Settings */}
                      <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-6">
                          <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-lg flex items-center justify-center">
                            <div className="w-4 h-4 bg-white rounded-full"></div>
                          </div>
                          <h6 className="text-lg font-semibold text-gray-800">Interaction Settings</h6>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hover Effect</label>
                            <select
                              className="w-full p-3 border-2 border-yellow-200 rounded-lg focus:border-yellow-500 focus:ring-0 transition-colors"
                              value={image.hoverEffect || 'zoom'}
                              onChange={(e) => updateImage(index, 'hoverEffect', e.target.value)}
                            >
                              <option value="none">None</option>
                              <option value="zoom">Zoom</option>
                              <option value="fade">Fade</option>
                              <option value="slide">Slide</option>
                              <option value="rotate">Rotate</option>
                              <option value="scale-rotate">Scale Rotate</option>
                              <option value="lift">Lift</option>
                              <option value="tilt">Tilt</option>
                              <option value="glow">Glow</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Click Action</label>
                            <select
                              className="w-full p-3 border-2 border-yellow-200 rounded-lg focus:border-yellow-500 focus:ring-0 transition-colors"
                              value={image.onClickAction || 'modal'}
                              onChange={(e) => updateImage(index, 'onClickAction', e.target.value)}
                            >
                              <option value="none">None</option>
                              <option value="modal">Open Modal</option>
                              <option value="link">Open Link</option>
                              <option value="download">Download</option>
                            </select>
                          </div>
                        </div>

                        {/* Link URL for click action */}
                        {image.onClickAction === 'link' && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Link URL</label>
                            <input
                              type="text"
                              className="w-full p-3 border-2 border-yellow-200 rounded-lg focus:border-yellow-500 focus:ring-0 transition-colors"
                              value={image.linkUrl || ''}
                              onChange={(e) => updateImage(index, 'linkUrl', e.target.value)}
                              placeholder="https://example.com"
                            />
                          </div>
                        )}
                      </div>

                      {/* Additional Settings */}
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-6">
                          <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center">
                            <div className="w-4 h-4 bg-white rounded-sm transform rotate-12"></div>
                          </div>
                          <h6 className="text-lg font-semibold text-gray-800">Additional Settings</h6>
                        </div>

                        <div className="space-y-6">
                          {/* Toggle Options */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-center gap-3 p-4 bg-white rounded-lg border border-indigo-200 cursor-pointer hover:bg-indigo-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={image.showOverlay || false}
                                onChange={(e) => updateImage(index, 'showOverlay', e.target.checked)}
                                className="w-5 h-5 text-indigo-600 border-2 border-gray-300 rounded focus:ring-indigo-500"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-800">Show Overlay on Hover</span>
                                <p className="text-xs text-gray-600">Display overlay text when hovering</p>
                              </div>
                            </label>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Priority (Loading Order)</label>
                              <input
                                type="number"
                                className="w-full p-3 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                                value={image.priority || '0'}
                                onChange={(e) => updateImage(index, 'priority', parseInt(e.target.value))}
                                min="0"
                                max="10"
                                placeholder="0-10 (higher loads first)"
                              />
                            </div>
                          </div>

                          {/* Overlay Settings */}
                          {image.showOverlay && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-indigo-200">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Text</label>
                                <input
                                  type="text"
                                  className="w-full p-3 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                                  value={image.overlayText || ''}
                                  onChange={(e) => updateImage(index, 'overlayText', e.target.value)}
                                  placeholder="View Image"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Color</label>
                                <input
                                  type="color"
                                  className="w-full h-12 border-2 border-indigo-200 rounded-lg cursor-pointer"
                                  value={image.overlayColor || '#000000'}
                                  onChange={(e) => updateImage(index, 'overlayColor', e.target.value)}
                                />
                              </div>
                            </div>
                          )}

                          {/* Advanced Options */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Loading Behavior</label>
                              <select
                                className="w-full p-3 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                                value={image.loadingBehavior || 'lazy'}
                                onChange={(e) => updateImage(index, 'loadingBehavior', e.target.value)}
                              >
                                <option value="lazy">Lazy Load</option>
                                <option value="eager">Load Immediately</option>
                                <option value="auto">Auto</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Custom CSS Class</label>
                              <input
                                type="text"
                                className="w-full p-3 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-0 transition-colors"
                                value={image.customClass || ''}
                                onChange={(e) => updateImage(index, 'customClass', e.target.value)}
                                placeholder="custom-image-class"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Controls */}
                      <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-slate-500 rounded-lg flex items-center justify-center">
                            <div className="w-4 h-4 bg-white rounded-full"></div>
                          </div>
                          <h6 className="text-lg font-semibold text-gray-800">Status Controls</h6>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <label className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={image.isActive !== false}
                              onChange={(e) => updateImage(index, 'isActive', e.target.checked)}
                              className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-800">Active</span>
                              <p className="text-xs text-gray-600">Item is visible in gallery</p>
                            </div>
                          </label>

                          <label className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={image.clickable !== false}
                              onChange={(e) => updateImage(index, 'clickable', e.target.checked)}
                              className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-800">Clickable</span>
                              <p className="text-xs text-gray-600">Users can interact with item</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Preview Section */}
                      {image.url && (
                        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6 border-2 border-dashed border-teal-300">
                          <div className="text-center mb-4">
                            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm text-sm border border-teal-200">
                              <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                              <span className="text-gray-700 font-medium">Live Preview</span>
                            </div>
                          </div>

                          <div className="flex justify-center mb-4">
                            <div
                              className="preview-container bg-white rounded-lg shadow-sm border border-teal-200"
                              style={{
                                width: image.containerWidth || 'auto',
                                height: image.containerHeight || 'auto',
                                padding: image.containerPadding || '0',
                                margin: image.containerMargin || '0',
                                backgroundColor: image.containerBackground || 'transparent',
                                borderRadius: image.containerBorderRadius || '0',
                                position: image.containerPosition || 'relative',
                                zIndex: image.containerZIndex || 'auto',
                                overflow: image.containerOverflowHidden ? 'hidden' : 'visible',
                                isolation: image.containerIsolate ? 'isolate' : 'auto',
                                boxShadow: image.containerShadow === 'small' ? '0 1px 3px rgba(0,0,0,0.1)' :
                                  image.containerShadow === 'medium' ? '0 4px 6px rgba(0,0,0,0.1)' :
                                    image.containerShadow === 'large' ? '0 10px 15px rgba(0,0,0,0.1)' :
                                      image.containerShadow === 'custom' ? image.customContainerShadow :
                                        'none',
                                maxWidth: '100%',
                                maxHeight: '200px'
                              }}
                            >
                              {image.type === 'video' ? (
                                <video
                                  src={image.url}
                                  className="max-w-full h-auto mx-auto rounded"
                                  controls
                                  style={{
                                    width: image.width || '100%',
                                    height: image.height || 'auto',
                                    objectFit: image.objectFit || 'cover'
                                  }}
                                />
                              ) : (
                                <img
                                  src={image.url}
                                  alt={image.alt || `Gallery image ${index + 1}`}
                                  className="max-w-full h-auto mx-auto rounded"
                                  style={{
                                    width: image.width || '100%',
                                    height: image.height || 'auto',
                                    objectFit: image.objectFit || 'cover'
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-white p-3 rounded-lg shadow-sm border border-teal-200 text-center">
                              <div className="text-xs text-gray-600 mb-1">Container</div>
                              <div className="text-xs font-medium text-teal-600">
                                {image.containerWidth || 'auto'} × {image.containerHeight || 'auto'}
                              </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg shadow-sm border border-teal-200 text-center">
                              <div className="text-xs text-gray-600 mb-1">Media</div>
                              <div className="text-xs font-medium text-teal-600">
                                {image.width || '100%'} × {image.height || 'auto'}
                              </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg shadow-sm border border-teal-200 text-center">
                              <div className="text-xs text-gray-600 mb-1">Effect</div>
                              <div className="text-xs font-medium text-teal-600">
                                {image.hoverEffect || 'zoom'}
                              </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg shadow-sm border border-teal-200 text-center">
                              <div className="text-xs text-gray-600 mb-1">Action</div>
                              <div className="text-xs font-medium text-teal-600">
                                {image.onClickAction || 'modal'}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>


     

         

        </div>



      </div>

    </div>
  );

};

export default GalleryForm;